import React, { FC, useState, useMemo, useEffect, useCallback } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import {
  getGoodsReceiptById,
  getGoodsReceipts,
  getInvoiceById,
  getInvoices,
  getOwnerApprovalQueuePage,
  getPurchaseOrderById,
  getPurchaseOrderDocumentById,
  getPurchaseOrdersPage,
  getQuotationsByRFQPage,
  getRFQById,
  getRFQsPage,
  getRequisitions,
  getRequisitionById,
  getPurchaseOrderApprovalSummaries,
  getApprovalInstanceByDocument,
  actOnApprovalInstance,
  ownerApprovePurchaseOrder,
  ownerRejectPurchaseOrder,
  ownerReworkPurchaseOrder,
  submitPurchaseOrderForApproval,
  sendPurchaseOrder,
  getRequisitionFormInventory,
  updateCompanyPurchaseOrderLines,
  approveVendorLogisticsChange,
  rejectVendorLogisticsChange,
  getAccountingAccounts,
} from '../core/_requests'
import {
  AccountingAccount,
  ApprovalAvailableActions,
  ApprovalInstance,
  GoodsReceipt,
  Invoice,
  PurchaseOrder,
  PurchaseOrderListRow,
} from '../core/_models'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { CreateReceiptModal } from './GoodsReceipt'
import PurchaseOrderPARUDocument from './PurchaseOrderPARUDocument'
import ProcurementFilterPanel, { FilterOption } from './ProcurementFilterPanel'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { downloadElementAsPdf } from '../core/exportUtils'
import MoneyDisplay from './MoneyDisplay'
import ProcurementActionGrid from './ProcurementActionGrid'
import RequisitionLECRDocument from './RequisitionLECRDocument'
import QuotationDPCDocument from './QuotationDPCDocument'
import GoodsReceiptGRNDocument from './GoodsReceiptGRNDocument'
import InvoiceProcurementDocument from './InvoiceProcurementDocument'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import ApprovalDocumentStatusCard from './ApprovalDocumentStatusCard'
import { resolvePurchaseOrderMoneyPresentation } from '../core/purchaseOrderMoney'
import DirectPurchaseOrderModal from './DirectPurchaseOrderModal'
import SearchableLookupField from './SearchableLookupField'
import {buildRequisitionDownloadDoc, RequisitionDownloadDoc} from '../core/requisitionDownload'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import axios from 'axios'
import { setupAxios } from '../../auth/core/AuthHelpers'

const accountsApi = axios.create({baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || ''})
if (typeof setupAxios === 'function') {
  setupAxios(accountsApi)
}

const resolveVesselName = (source: any): string => {
  const requisition = source?.rfq?.requisition || source?.requisition || source
  const candidates = [
    source?.vesselName,
    source?.vessel_name,
    source?.vessel?.fleetName,
    source?.vessel?.fleet_name,
    source?.vessel?.name,
    requisition?.vesselName,
    requisition?.vessel_name,
    requisition?.vessel?.fleet_name,
    requisition?.vessel?.fleetName,
    requisition?.vessel?.name,
    requisition?.vessel?.vesselName,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }
  return ''
}

const normalizePoSearchValue = (value: any): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const isDirectPurchaseOrder = (purchaseOrder?: PurchaseOrder | null) =>
  String(purchaseOrder?.sourceType || '').toUpperCase() === 'DIRECT_PO'

const formatLogisticsAmount = (value: number | null | undefined, currency?: string | null) => {
  const amount = Number(value ?? 0)
  const code = String(currency || 'USD').trim().toUpperCase() || 'USD'
  return `${code} ${amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
}

const toProcurementListFilters = (globalState: {
  company: {id: number; type: string} | null
  vessel: {id?: number | null} | null
}) => ({
  ...(globalState.company?.id ? {companyId: globalState.company.id, companyType: globalState.company.type} : {}),
  ...(globalState.vessel?.id ? {vesselId: Number(globalState.vessel.id)} : {}),
})

type BulkDocType = 'PO' | 'RFQ' | 'REQUISITION' | 'GR' | 'INVOICE'

type ApprovalSnapshot = {
  latest: ApprovalInstance | null
  availability: ApprovalAvailableActions | null
}

const approvalStatusBadgeClass = (status?: string) => {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'APPROVED') return 'bg-success'
  if (normalized === 'REJECTED' || normalized === 'CANCELLED') return 'bg-danger'
  if (normalized === 'REWORK') return 'bg-warning text-dark'
  if (normalized === 'PENDING') return 'bg-info'
  return 'bg-secondary'
}

const formatApprovalStepLabel = (sequence?: number | null) =>
  sequence != null && Number.isFinite(Number(sequence)) ? `Step ${sequence}` : 'No active step'

const canShowSendToVendorAction = (
  record: PurchaseOrder | PurchaseOrderListRow,
  approval?: ApprovalSnapshot | null
) => {
  const poStatus = String(record.status || '').toUpperCase()
  if (poStatus !== 'APPROVED') {
    return false
  }
  if (record.ownerApprovalRequired === true) {
    return true
  }
  if (!approval) {
    return false
  }
  const approvalStatus = String(approval.latest?.status || '').toUpperCase()
  return approvalStatus !== 'PENDING'
}

interface CompanyPurchaseOrderLineEditModalProps {
  visible: boolean
  purchaseOrder: PurchaseOrder | null
  onClose: () => void
  onSuccess: () => Promise<void>
}

type CompanyPoLineState = {
  id: number | null
  item: string
  quantity: string
  inventoryItemId: number | null
  masterInventoryItemId: number | null
  partId: number | null
  unitOfMeasurement: string | null
  manualItemName: string | null
  unitPrice: string
}

const CompanyPurchaseOrderLineEditModal: FC<CompanyPurchaseOrderLineEditModalProps> = ({
  visible,
  purchaseOrder,
  onClose,
  onSuccess,
}) => {
  const [lines, setLines] = useState<CompanyPoLineState[]>([])
  const [itemOptions, setItemOptions] = useState<any[]>([])
  const itemSearchCache = React.useRef<Record<string, any[]>>({})
  const itemSearchSequence = React.useRef(0)
  const [saving, setSaving] = useState(false)

  const vesselId = Number(purchaseOrder?.vessel_id || purchaseOrder?.vesselId || purchaseOrder?.rfq?.requisition?.vesselId || 0)

  useEffect(() => {
    if (!visible || !purchaseOrder) return
    const nextLines = (purchaseOrder.quotationLines || []).map((line: any) => ({
      id: line.requisitionLineId != null ? Number(line.requisitionLineId) : null,
      item: String(line.manualItemName || line.part?.name || line.subcomponent?.name || '').trim(),
      quantity: String(line.quantity ?? line.assignedQuantity ?? ''),
      inventoryItemId: line.inventoryItemId != null ? Number(line.inventoryItemId) : null,
      masterInventoryItemId: line.masterInventoryItemId != null ? Number(line.masterInventoryItemId) : null,
      partId: line.partId != null ? Number(line.partId) : null,
      unitOfMeasurement: line.unitOfMeasurement || null,
      manualItemName: line.inventoryItemId == null ? String(line.manualItemName || '').trim() || null : null,
      unitPrice: String(line.unitPrice ?? ''),
    }))
    setLines(nextLines)
    setItemOptions(nextLines.filter((line) => line.inventoryItemId != null).map((line) => ({
      id: line.inventoryItemId,
      itemName: line.item,
      unitOfMeasurement: line.unitOfMeasurement,
    })))
    itemSearchCache.current = {}
  }, [visible, purchaseOrder])

  if (!visible || !purchaseOrder) return null
  const canEditLines = ['DRAFT', 'SENT_TO_VENDOR'].includes(String(purchaseOrder.status || '').toUpperCase())
  const canRemoveLines = canEditLines

  const updateLine = (index: number, patch: Partial<CompanyPoLineState>) => {
    setLines((current) => current.map((line, lineIndex) => lineIndex === index ? {...line, ...patch} : line))
  }

  const addLine = () => {
    setLines((current) => [...current, {
      id: null,
      item: '',
      quantity: '1',
      inventoryItemId: null,
      masterInventoryItemId: null,
      partId: null,
      unitOfMeasurement: 'EA',
      manualItemName: null,
      unitPrice: '',
    }])
  }

  const loadItemOptions = async (search: string) => {
    if (!vesselId) return
    const query = search.trim().toLowerCase()
    if (itemSearchCache.current[query]) {
      setItemOptions((current) => Array.from(new Map([...current, ...itemSearchCache.current[query]].map((item) => [item.id, item])).values()))
      return
    }
    const sequence = ++itemSearchSequence.current
    try {
      const options = await getRequisitionFormInventory({vesselId, q: search.trim() || undefined, limit: 50})
      itemSearchCache.current[query] = options
      if (sequence !== itemSearchSequence.current) return
      setItemOptions((current) => Array.from(new Map([...current, ...options].map((item) => [item.id, item])).values()))
    } catch {
      // Keep already loaded/current line options available if lookup fails.
    }
  }

  const applySelectedItem = (index: number, selected: any) => {
    if (!selected) return
    updateLine(index, {
      item: `${selected.itemCode || ''} ${selected.itemName || ''}`.trim(),
      inventoryItemId: Number(selected.id),
      masterInventoryItemId: selected.masterInventoryItemId != null ? Number(selected.masterInventoryItemId) : null,
      partId: selected.partId != null ? Number(selected.partId) : null,
      unitOfMeasurement: selected.unitOfMeasurement || null,
      manualItemName: null,
    })
  }

  const save = async () => {
    if (!lines.length) {
      toast.error('At least one purchase order line is required')
      return
    }
    const payload = lines.map((line) => ({
      requisitionLineId: line.id,
      inventoryItemId: line.inventoryItemId,
      masterInventoryItemId: line.masterInventoryItemId,
      partId: line.partId,
      unitOfMeasurement: line.unitOfMeasurement,
      manualItemName: line.manualItemName || line.item.trim() || null,
      quantity: Number(line.quantity),
      unitPrice: line.unitPrice === '' ? null : Number(line.unitPrice),
    }))
    if (payload.some((line) => (!line.inventoryItemId && !line.manualItemName) || !Number.isFinite(line.quantity) || line.quantity <= 0 || (line.unitPrice != null && (!Number.isFinite(line.unitPrice) || line.unitPrice < 0)))) {
      toast.error('Each line requires an item, quantity greater than zero, and a valid rate')
      return
    }
    setSaving(true)
    try {
      await updateCompanyPurchaseOrderLines(purchaseOrder.id, payload)
      toast.success('Purchase order lines updated')
      await onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update purchase order lines')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='modal fade show d-flex align-items-center justify-content-center' style={{backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 1050, padding: '1rem'}}>
      <div className='modal-dialog modal-dialog-centered m-0' style={{width: '88vw', maxWidth: 1300, maxHeight: '85vh'}}>
        <div className='modal-content bg-white' style={{height: '85vh', maxHeight: '85vh', display: 'flex'}}>
          <div className='modal-header py-3 flex-shrink-0'>
            <div>
              <h5 className='modal-title mb-1'>Edit Purchase Order Lines</h5>
              <div className='text-muted small'>
                PO Number: <span className='fw-semibold text-dark'>{purchaseOrder.poNumber}</span>
                <span className='mx-2'>•</span>
                Status: <span className='fw-semibold text-dark'>{purchaseOrder.status}</span>
                <span className='mx-2'>•</span>
                Lines: <span className='fw-semibold text-dark'>{lines.length}</span>
              </div>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <button type='button' className='btn btn-sm btn-light-primary' onClick={addLine} disabled={!canEditLines || saving}>Add item</button>
              <button type='button' className='btn-close' onClick={onClose} disabled={saving} />
            </div>
          </div>
          <div className='modal-body d-flex flex-column min-h-0' style={{overflow: 'hidden'}}>
            <div className='alert alert-info py-2 mb-3 flex-shrink-0'>Items, quantity, UOM, and rate can be changed or added while the PO is Draft or Sent to Vendor.</div>
            <div className='flex-grow-1' style={{overflowY: 'auto', overflowX: 'auto'}}>
              <div style={{minWidth: canRemoveLines ? 700 : 580}}>
                <div
                  className='text-muted small fw-semibold border-bottom pb-2 mb-1'
                  style={{display: 'grid', gridTemplateColumns: canRemoveLines ? '48px minmax(320px, 1fr) 100px 100px 100px' : '48px minmax(320px, 1fr) 100px 100px', gap: '0.75rem', alignItems: 'center'}}
                >
                  <span>Line</span>
                  <span>Item</span>
                  <span>Quantity</span>
                  <span>Rate</span>
                  {canRemoveLines && <span>Action</span>}
                </div>
                {lines.map((line, index) => (
                  <div
                    key={`${line.id ?? 'new'}-${index}`}
                    className='border-bottom py-2'
                    style={{display: 'grid', gridTemplateColumns: canRemoveLines ? '48px minmax(320px, 1fr) 100px 100px 100px' : '48px minmax(320px, 1fr) 100px 100px', gap: '0.75rem', alignItems: 'center'}}
                  >
                    <span className='text-muted small'>{index + 1}</span>
                    <SearchableLookupField
                      label='Item'
                      showLabel={false}
                      value={line.inventoryItemId != null ? String(line.inventoryItemId) : ''}
                      searchText={line.item}
                      displayValue={line.item}
                      options={itemOptions.map((option) => ({
                        id: option.id,
                        label: `${option.itemCode || ''} ${option.itemName || ''}`.trim(),
                      }))}
                      onSearchTextChange={(value) => {
                        const replacingCanonicalItem = line.inventoryItemId != null
                        updateLine(index, {
                          item: value,
                          inventoryItemId: replacingCanonicalItem ? null : line.inventoryItemId,
                          masterInventoryItemId: replacingCanonicalItem ? null : line.masterInventoryItemId,
                          partId: replacingCanonicalItem ? null : line.partId,
                          unitOfMeasurement: replacingCanonicalItem ? null : line.unitOfMeasurement,
                          manualItemName: replacingCanonicalItem ? null : value,
                        })
                        void loadItemOptions(value)
                      }}
                      onOptionSelect={(option) => {
                        applySelectedItem(index, itemOptions.find((candidate) => String(candidate.id) === String(option.id)))
                      }}
                      onChange={(value) => {
                        applySelectedItem(index, itemOptions.find((option) => String(option.id) === String(value)))
                      }}
                      placeholder={line.inventoryItemId == null ? 'Type to search or edit manual item' : 'Search inventory items'}
                      listId={`company-po-line-item-${line.id}`}
                      wrapperClassName='w-100'
                      inputClassName='form-control-sm'
                    />
                    <input
                      aria-label={`Quantity for line ${index + 1}`}
                      type='number'
                      min='0.000001'
                      step='any'
                      className='form-control form-control-sm'
                      value={line.quantity}
                      onChange={(event) => setLines((current) => current.map((entry, i) => i === index ? {...entry, quantity: event.target.value} : entry))}
                    />
                    <input
                      aria-label={`Rate for line ${index + 1}`}
                      type='number'
                      min='0'
                      step='any'
                      className='form-control form-control-sm'
                      value={line.unitPrice}
                      onChange={(event) => updateLine(index, {unitPrice: event.target.value})}
                    />
                    {canRemoveLines && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger'
                        onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className='modal-footer py-3 flex-shrink-0'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={saving}>Cancel</button>
            <button type='button' className='btn btn-primary' onClick={() => void save()} disabled={saving}>Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ViewPurchaseOrderModalProps {
  visible: boolean
  onClose: () => void
  purchaseOrder: PurchaseOrder | null
  onDownload?: () => void
  onApprovalChanged?: () => void | Promise<void>
  canReviewVendorLogistics?: boolean
  onReviewVendorLogistics?: (action: 'accept' | 'reject') => void | Promise<void>
}

const ViewPurchaseOrderModal: FC<ViewPurchaseOrderModalProps> = ({
  visible,
  onClose,
  purchaseOrder,
  onDownload,
  onApprovalChanged,
  canReviewVendorLogistics = false,
  onReviewVendorLogistics,
}) => {
  if (!visible || !purchaseOrder) return null

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { cls: string; text: string } } = {
      'DRAFT': { cls: 'bg-light-info text-info', text: 'Draft' },
      'SENT_TO_VENDOR': { cls: 'bg-primary text-white', text: 'Sent to Vendor' },
      'PENDING_OWNER_APPROVAL': { cls: 'bg-warning text-dark', text: 'Pending Owner Approval' },
      'PENDING_APPROVAL': { cls: 'bg-warning text-dark', text: 'Pending Approval' },
      'APPROVED': { cls: 'bg-success text-white', text: 'Approved' },
      'REWORK': { cls: 'bg-light-warning text-warning', text: 'Owner Rework' },
      'REJECTED': { cls: 'bg-danger text-white', text: 'Rejected' },
      'INVOICE_RECEIVED': { cls: 'bg-dark text-white', text: 'Invoice Received' },
      'PARTIALLY_DELIVERED': { cls: 'bg-light-warning text-warning', text: 'Partially Delivered' },
      'DELIVERED': { cls: 'bg-success text-white', text: 'Delivered' },
      'CONFIRMED': { cls: 'bg-info text-white', text: 'Confirmed' },
      'COMPLETED': { cls: 'bg-info text-white', text: 'Completed' }
    }
    const statusInfo = statusMap[status] || { cls: 'bg-light-dark text-dark', text: status }
    return <span className={`badge ${statusInfo.cls}`}>{statusInfo.text}</span>
  }

  const hasVendorLogisticsRequest =
    String(purchaseOrder.vendorLogisticsChangeStatus || '').toUpperCase() !== 'NONE' &&
    Boolean(purchaseOrder.vendorLogisticsChangeStatus)
  const logisticsCurrency = purchaseOrder.currency || purchaseOrder.quotationCurrency
  const currentLogisticsRequestTotal = [
    purchaseOrder.freightCost,
    purchaseOrder.packagingCost,
    purchaseOrder.truckCost,
    purchaseOrder.bargeCost,
    purchaseOrder.otherCost,
    purchaseOrder.vatOnLogistics,
  ].reduce((sum, value) => sum + Number(value ?? 0), 0)
  const requestedLogisticsRequestTotal = [
    purchaseOrder.proposedFreightCost,
    purchaseOrder.proposedPackagingCost,
    purchaseOrder.proposedTruckCost,
    purchaseOrder.proposedBargeCost,
    purchaseOrder.proposedOtherCost,
    purchaseOrder.proposedVatOnLogistics,
  ].reduce((sum, value) => sum + Number(value ?? 0), 0)
  const currentTotalPrice = Number(purchaseOrder.totalAmount ?? 0)
  const requestedTotalPrice =
    currentTotalPrice - currentLogisticsRequestTotal + requestedLogisticsRequestTotal

  const vendorLogisticsChangeRequestContent = hasVendorLogisticsRequest ? (
    <div className='card border border-warning mb-6'>
      <div className='card-header bg-light-warning py-3'>
        <div className='d-flex justify-content-between align-items-center'>
          <h5 className='mb-0 text-dark'>Vendor Logistics Change Request</h5>
          <div className='d-flex align-items-center gap-2'>
            <span className='badge bg-warning text-dark'>
              {String(purchaseOrder.vendorLogisticsChangeStatus).replaceAll('_', ' ')}
            </span>
            {canReviewVendorLogistics &&
              String(purchaseOrder.vendorLogisticsChangeStatus).toUpperCase() === 'PENDING' && (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-success procurement-print-hide'
                  onClick={() => void onReviewVendorLogistics?.('accept')}
                >
                  Approve
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-danger procurement-print-hide'
                  onClick={() => void onReviewVendorLogistics?.('reject')}
                >
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-sm align-middle mb-3'>
            <thead>
              <tr>
                <th>Cost Component</th>
                <th className='text-end'>Current</th>
                <th className='text-end'>Requested</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Trucking/Freight Cost', purchaseOrder.freightCost, purchaseOrder.proposedFreightCost],
                ['Packaging & Handling Cost', purchaseOrder.packagingCost, purchaseOrder.proposedPackagingCost],
                ['Truck Cost', purchaseOrder.truckCost, purchaseOrder.proposedTruckCost],
                ['Barge/Workboat Cost', purchaseOrder.bargeCost, purchaseOrder.proposedBargeCost],
                ['Other Cost', purchaseOrder.otherCost, purchaseOrder.proposedOtherCost],
                ['VAT/GST on Logistics', purchaseOrder.vatOnLogistics, purchaseOrder.proposedVatOnLogistics],
              ].map(([label, current, requested]) => (
                <tr key={String(label)}>
                  <td>{label}</td>
                    <td className='text-end'>{formatLogisticsAmount(current as number | null, logisticsCurrency)}</td>
                    <td className='text-end fw-bold bg-light-warning'>
                    {formatLogisticsAmount(requested as number | null, logisticsCurrency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {purchaseOrder.vendorLogisticsChangeRemark && (
          <div className='small'>
            <span className='fw-semibold'>Vendor remark:</span>{' '}
            {purchaseOrder.vendorLogisticsChangeRemark}
          </div>
        )}
        <div className='row g-2 mt-3'>
          <div className='col-md-4'>
            <div className='border rounded p-2 bg-light'>
              <div className='small text-muted'>Current Total Price</div>
              <div className='fw-bold'>{formatLogisticsAmount(currentTotalPrice, logisticsCurrency)}</div>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='border rounded p-2 bg-light-warning'>
              <div className='small text-muted'>Requested Total Price</div>
              <div className='fw-bold'>{formatLogisticsAmount(requestedTotalPrice, logisticsCurrency)}</div>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='border rounded p-2 bg-light'>
              <div className='small text-muted'>Difference</div>
              <div className='fw-bold'>
                {formatLogisticsAmount(requestedTotalPrice - currentTotalPrice, logisticsCurrency)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center procurement-document-modal"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div
        className='modal-dialog modal-xl modal-dialog-centered procurement-view-modal-dialog'
        role='document'
      >
        <div className='modal-content procurement-document-modal-content bg-white'>
          <div className='modal-header border-bottom procurement-print-hide'>
            <div>
              <h3 className='modal-title fw-bold text-dark mb-1'>Purchase Order Details</h3>
              <p className='text-muted mb-0 fs-7'>{purchaseOrder.poNumber}</p>
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='modal-body procurement-document-modal-body'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div className='d-flex align-items-center gap-2'>
                {getStatusBadge(purchaseOrder.status)}
                {isDirectPurchaseOrder(purchaseOrder) && (
                  <span className='badge bg-light-info text-info'>Direct PO</span>
                )}
              </div>
              <div className='d-flex align-items-center gap-2 procurement-print-hide'>
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={onDownload}
                >
                  Download PDF
                </button>
              </div>
            </div>
            {purchaseOrder.ownerApprovalRequired ? (
              <div className='alert alert-info mb-6'>
                This purchase order uses the legacy owner approval workflow.
              </div>
            ) : (
              <div className='mb-6'>
                <ApprovalDocumentStatusCard
                  moduleType='PURCHASE_ORDER'
                  documentId={Number(purchaseOrder.id)}
                  title='Purchase Order Approval'
                  onChanged={onApprovalChanged}
                />
              </div>
            )}
            <PurchaseOrderPARUDocument
              purchaseOrder={purchaseOrder}
              vendorLogisticsChangeRequestContent={vendorLogisticsChangeRequestContent}
            />
          </div>

          <div className='modal-footer border-top procurement-print-hide'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CreatePurchaseOrderModalProps {
  visible: boolean
  onClose: () => void
  onOpenQuotationManagement: () => void
  canCreateDirectPO: boolean
  onCreateDirectPO: () => void
}

const CreatePurchaseOrderModal: FC<CreatePurchaseOrderModalProps> = ({
  visible,
  onClose,
  onOpenQuotationManagement,
  canCreateDirectPO,
  onCreateDirectPO,
}) => {
  const handleClose = () => {
    onClose()
  }

  if (!visible) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-dialog-centered' role='document'>
        <div className='modal-content bg-white'>
          <div className='modal-header'>
            <h5 className='modal-title'>Create New Purchase Order</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body'>
            <div className='border rounded p-4 mb-4'>
              <h6 className='fw-bold mb-2'>Create from Quotation</h6>
              <p className='mb-3 text-dark'>
                Use the normal requisition/RFQ/quotation workflow.
              </p>
              <button
                type='button'
                className='btn btn-primary btn-sm'
                onClick={onOpenQuotationManagement}
              >
                Open Quotation Management
              </button>
            </div>
            {canCreateDirectPO && (
              <div className='border rounded p-4'>
                <h6 className='fw-bold mb-2'>Create Direct PO</h6>
                <p className='mb-3 text-dark'>
                  Create requisition, RFQ, quotation, and purchase order from one form.
                </p>
                <button
                  type='button'
                  className='btn btn-light-primary btn-sm'
                  onClick={onCreateDirectPO}
                >
                  Create Direct PO
                </button>
              </div>
            )}
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const PurchaseOrdersList: FC = () => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { state: globalState } = useGlobalFilter()
  const globalCompanyId = globalState.company?.id ?? null
  const globalCompanyType = globalState.company?.type ?? null
  const globalVesselId = globalState.vessel?.id ? Number(globalState.vessel.id) : null
  const isVendorView = String(currentUser?.role?.roleType || currentUser?.roleType || '').toUpperCase() === 'VENDOR'
  const {
    can: canProcurementPermission,
    loading: procurementPermissionsLoading,
  } = useProcurementPermissions()
  const canViewPO = canProcurementPermission(PROCUREMENT_PERMS.READ_PURCHASE_ORDER)
  const canCreatePO = canProcurementPermission(PROCUREMENT_PERMS.CREATE_PURCHASE_ORDER)
  const canCreateDirectPO = canProcurementPermission(PROCUREMENT_PERMS.PO_DIRECT_CREATE)
  const canUpdatePO = canProcurementPermission(PROCUREMENT_PERMS.UPDATE_PURCHASE_ORDER)
  const canCreateGR = canProcurementPermission(PROCUREMENT_PERMS.CREATE_GOODS_RECEIPT)
  const canDownloadPO =
    canProcurementPermission(PROCUREMENT_PERMS.PO_DOWNLOAD) ||
    canProcurementPermission(PROCUREMENT_PERMS.READ_PURCHASE_ORDER)
  const canExportPO =
    canProcurementPermission(PROCUREMENT_PERMS.PO_EXPORT) || canDownloadPO
  const canDownloadRFQ =
    canProcurementPermission(PROCUREMENT_PERMS.RFQ_DOWNLOAD) ||
    canProcurementPermission(PROCUREMENT_PERMS.RFQ_VIEW)
  const canDownloadRequisition = canProcurementPermission(PROCUREMENT_PERMS.READ_REQUISITION)
  const canDownloadGR =
    canProcurementPermission(PROCUREMENT_PERMS.GRN_DOWNLOAD) ||
    canProcurementPermission(PROCUREMENT_PERMS.READ_GOODS_RECEIPT)
  const canDownloadInvoice = canProcurementPermission(PROCUREMENT_PERMS.INVOICE_VIEW)
  const canDownloadAny =
    canDownloadPO || canDownloadRFQ || canDownloadRequisition || canDownloadGR || canDownloadInvoice
  const canSendPOPermission = canProcurementPermission(PROCUREMENT_PERMS.PO_SEND)
  const canSubmitPOApprovalPermission = canProcurementPermission(PROCUREMENT_PERMS.PO_SUBMIT_APPROVAL)
  const canApprovePOPermission = canProcurementPermission(PROCUREMENT_PERMS.PO_APPROVE)
  const canRejectPOPermission = canProcurementPermission(PROCUREMENT_PERMS.PO_REJECT)
  const canReviewVendorLogistics = canProcurementPermission(PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW)
  const canAccessOwnerQueue =
    canProcurementPermission(PROCUREMENT_PERMS.PO_OWNER_APPROVE) ||
    canProcurementPermission(PROCUREMENT_PERMS.PO_OWNER_REJECT)
  const canRunOwnerQueueActions = canAccessOwnerQueue
  const [searchTerm, setSearchTerm] = useState('')
  const [listMode, setListMode] = useState<'ALL' | 'OWNER_QUEUE'>('ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [purchaseOrderData, setPurchaseOrderData] = useState<PurchaseOrderListRow[]>([])
  const [budgetData, setBudgetData] = useState<any[]>([])
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([])
  const [serverTotalElements, setServerTotalElements] = useState(0)
  const [serverTotalPages, setServerTotalPages] = useState(0)
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isDirectPoModalVisible, setIsDirectPoModalVisible] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null)
  const [editingDirectPO, setEditingDirectPO] = useState<PurchaseOrder | null>(null)
  const [editingCompanyPO, setEditingCompanyPO] = useState<PurchaseOrder | null>(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [referenceFilter, setReferenceFilter] = useState('')
  const [vesselFilter, setVesselFilter] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [catalogueFilter, setCatalogueFilter] = useState('')
  const [requisitionTitleFilter, setRequisitionTitleFilter] = useState('')
  const [approvalPendingWithFilter, setApprovalPendingWithFilter] = useState('')
  const [poDateFromFilter, setPoDateFromFilter] = useState('')
  const [poDateToFilter, setPoDateToFilter] = useState('')
  const [deliveredToVesselFilter, setDeliveredToVesselFilter] = useState('')
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [approvalSnapshots, setApprovalSnapshots] = useState<Record<number, ApprovalSnapshot>>({})
  const [isGoodsReceiptModalVisible, setIsGoodsReceiptModalVisible] = useState(false)
  const [selectedPOForReceipt, setSelectedPOForReceipt] = useState<number | null>(null)
  const [ownerActionDialog, setOwnerActionDialog] = useState<{
    po: PurchaseOrder
    action: 'APPROVE' | 'REJECT' | 'REWORK'
  } | null>(null)
  const [ownerActionRemark, setOwnerActionRemark] = useState('')
  const [ownerActionSubmitting, setOwnerActionSubmitting] = useState(false)
  const [vendorLogisticsActionId, setVendorLogisticsActionId] = useState<number | null>(null)
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [bulkType, setBulkType] = useState<BulkDocType>('PO')
  const [bulkFrom, setBulkFrom] = useState('')
  const [bulkTo, setBulkTo] = useState('')
  const [bulkCompany, setBulkCompany] = useState('')
  const [bulkVessel, setBulkVessel] = useState('')
  const [bulkVendor, setBulkVendor] = useState('')
  const [bulkStatus, setBulkStatus] = useState('')
  const [isBulkDownloading, setIsBulkDownloading] = useState(false)
  const [poDownloadDoc, setPoDownloadDoc] = useState<PurchaseOrder | null>(null)
  const [rfqDownloadDoc, setRfqDownloadDoc] = useState<any | null>(null)
  const [requisitionDownloadDoc, setRequisitionDownloadDoc] = useState<RequisitionDownloadDoc | null>(null)
  const [goodsReceiptDownloadDoc, setGoodsReceiptDownloadDoc] = useState<{
    receipt: GoodsReceipt
  } | null>(null)
  const [invoiceDownloadDoc, setInvoiceDownloadDoc] = useState<{
    invoice: Invoice
    purchaseOrder?: PurchaseOrder | null
    goodsReceipt?: GoodsReceipt | null
    requisition?: any | null
  } | null>(null)
  const openedPoIdRef = React.useRef<number | null>(null)

  const canDownloadType = useCallback(
    (type: BulkDocType) => {
      switch (type) {
        case 'PO':
          return canDownloadPO
        case 'RFQ':
          return canDownloadRFQ
        case 'REQUISITION':
          return canDownloadRequisition
        case 'GR':
          return canDownloadGR
        case 'INVOICE':
          return canDownloadInvoice
        default:
          return false
      }
    },
    [canDownloadPO, canDownloadRFQ, canDownloadRequisition, canDownloadGR, canDownloadInvoice]
  )

  const availableBulkTypes = useMemo<BulkDocType[]>(
    () =>
      (['PO', 'RFQ', 'REQUISITION', 'GR', 'INVOICE'] as BulkDocType[]).filter((type) =>
        canDownloadType(type)
      ),
    [canDownloadType]
  )

  useEffect(() => {
    if (listMode === 'OWNER_QUEUE' && !canAccessOwnerQueue) {
      setListMode('ALL')
    }
  }, [canAccessOwnerQueue, listMode])

  useEffect(() => {
    setCurrentPage(1)
  }, [listMode])

  useEffect(() => {
    setCurrentPage(1)
  }, [globalCompanyId, globalCompanyType, globalVesselId])

  useEffect(() => {
    if (availableBulkTypes.length === 0) return
    if (!availableBulkTypes.includes(bulkType)) {
      setBulkType(availableBulkTypes[0])
    }
  }, [availableBulkTypes, bulkType])

  const loadPurchaseOrders = useCallback(async () => {
    try {
      setLoading(true)
      const pageIndex = Math.max(0, currentPage - 1)
      const listFilters = toProcurementListFilters(globalState)
      const data =
        listMode === 'OWNER_QUEUE'
          ? await getOwnerApprovalQueuePage(pageIndex, rowsPerPage)
          : await getPurchaseOrdersPage(pageIndex, rowsPerPage, listFilters)

      const content = data.content || []
      setPurchaseOrderData(content)
      setServerTotalElements(data.totalElements ?? content.length)
      setServerTotalPages(data.totalPages ?? 1)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading purchase orders')
      console.error('Error fetching purchase orders:', err)
    } finally {
      setLoading(false)
    }
  }, [currentPage, globalCompanyId, globalCompanyType, globalVesselId, listMode, rowsPerPage])

  const handleVendorLogisticsReview = useCallback(async (record: {id: number}, action: 'accept' | 'reject') => {
    if (!record.id || !canReviewVendorLogistics) return
    const promptResult = action === 'reject'
      ? window.prompt('Enter the rejection reason:')
      : window.prompt('Optional review remark:')
    // `null` means the reviewer clicked Cancel. Do not call the API.
    if (promptResult === null) return
    const remark = promptResult.trim()
    if (action === 'reject' && !remark) return

    try {
      setVendorLogisticsActionId(Number(record.id))
      if (action === 'accept') {
        await approveVendorLogisticsChange(record.id, remark || undefined)
      } else {
        await rejectVendorLogisticsChange(record.id, remark || '')
      }
      toast.success(action === 'accept' ? 'Vendor logistics change approved.' : 'Vendor logistics change rejected.')
      await loadPurchaseOrders()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || `Unable to ${action} vendor logistics change.`)
    } finally {
      setVendorLogisticsActionId(null)
    }
  }, [canReviewVendorLogistics, loadPurchaseOrders])

  const loadApprovalSnapshots = useCallback(async (records: Array<PurchaseOrder | PurchaseOrderListRow>) => {
    const ids = records
      .filter((record) => record.ownerApprovalRequired !== true)
      .map((record) => Number(record.id))
      .filter((id) => Number.isFinite(id) && id > 0)
    if (ids.length === 0) {
      setApprovalSnapshots({})
      return
    }
    try {
      const summaries = await getPurchaseOrderApprovalSummaries(ids)
      const entries = summaries.map((summary) => [
        Number(summary.purchaseOrderId),
        {
          latest: summary.currentApprovalStatus
            ? {
                status: summary.currentApprovalStatus,
                currentStepSequence: summary.currentApprovalStage ?? null,
              } as any
            : null,
          availability: {
            canSubmit: summary.canSubmit,
            canApprove: summary.canApprove,
            canReject: summary.canReject,
            canRework: summary.canRequestRework,
            canCancel: false,
            reason: summary.reason ?? null,
          },
        },
      ] as const)
      setApprovalSnapshots(Object.fromEntries(entries))
    } catch (error) {
      console.error('Error loading purchase order approval snapshots:', error)
      setApprovalSnapshots({})
    }
  }, [])

  const { vessels: globalVessels } = useGlobalFilter()

  const [poDetailsById, setPoDetailsById] = useState<Record<number, any>>({})

  const loadPoDetails = useCallback(async (records: Array<PurchaseOrder | PurchaseOrderListRow>) => {
    setPoDetailsById((prevDetails) => {
      const missingRecords = records.filter(
        (r) => r.id && !prevDetails[Number(r.id)] && !(r as any).budgetCode && !(r as any).budget_code && !(r as any).quotationLines?.length
      )
      if (missingRecords.length === 0) return prevDetails

      // Fire off async fetches outside setState
      void (async () => {
        console.log('[PO Budget] Loading details for', missingRecords.length, 'POs without account codes')
        const results = await Promise.allSettled(
          missingRecords.map(async (record) => {
            try {
              const detail = await getPurchaseOrderById(Number(record.id))
              return { id: Number(record.id), detail }
            } catch (err) {
              console.warn('[PO Budget] Failed to load PO detail for id:', record.id, err)
              return null
            }
          })
        )

        const newDetails: Record<number, any> = {}
        for (const res of results) {
          if (res.status === 'fulfilled' && res.value?.detail) {
            newDetails[res.value.id] = res.value.detail
          }
        }
        if (Object.keys(newDetails).length > 0) {
          console.log('[PO Budget] Loaded details for', Object.keys(newDetails).length, 'POs')
          setPoDetailsById((prev) => ({ ...prev, ...newDetails }))
        }
      })()

      return prevDetails // Return unchanged for now, async will update later
    })
  }, [])

  const loadBudgetData = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (globalState.company) {
        params.append('companyId', globalState.company.id.toString())
      }
      if (globalState.vessel) {
        params.append('vesselId', globalState.vessel.id.toString())
      } else if (globalState.company && globalVessels.length > 0) {
        params.append('vesselId', globalVessels.map((v: any) => v.id).join(','))
      }
      
      const queryStr = params.toString() ? `?${params.toString()}` : ''
      let budgets: any[] = []
      const tryUrls = [
        { instance: accountsApi, path: `/accounts/budgets${queryStr}` },
        { instance: axios, path: `/api/accounts/budgets${queryStr}` },
      ]
      for (const { instance, path } of tryUrls) {
        if (budgets.length > 0) break
        try {
          const res = await instance.get(path)
          if (Array.isArray(res.data) && res.data.length > 0) {
            budgets = res.data
            console.log('[PO Budget] Loaded', budgets.length, 'budgets from', path)
            if (budgets.length > 0) {
              const sample = budgets[0]
              console.log('[PO Budget] Sample budget keys:', Object.keys(sample).join(', '))
              console.log('[PO Budget] Sample accountCodes:', sample.accountCodes || sample.account_codes)
              console.log('[PO Budget] Sample vesselId:', sample.vesselId || sample.vessel_id)
              console.log('[PO Budget] Sample baseBudget:', sample.baseBudget, 'spentAmount:', sample.spentAmount, 'remainingAmount:', sample.remainingAmount)
            }
          }
        } catch (err: any) {
          console.warn('[PO Budget] Failed to fetch budgets from', path, err?.message || err)
        }
      }
      console.log('[PO Budget] Final budget count:', budgets.length)
      setBudgetData(budgets)
    } catch (err) {
      console.error('Error fetching budgets for PO list:', err)
    }

    try {
      const accounts = await getAccountingAccounts()
      if (Array.isArray(accounts)) {
        setAccountingAccounts(accounts)
      }
    } catch {
      // non-blocking
    }
  }, [globalState.company, globalState.vessel, globalVessels])

  useEffect(() => {
    void loadBudgetData()
  }, [loadBudgetData])

  const accountCodeById = useMemo(() => {
    const map = new Map<number, string>()
    accountingAccounts.forEach((acc) => {
      if (acc.id && acc.accountCodes) {
        map.set(Number(acc.id), String(acc.accountCodes).trim())
      }
    })
    return map
  }, [accountingAccounts])

  const formatBudgetCurrency = useCallback((val: any) => {
    const safeValue = Number.isFinite(Number(val)) ? Number(val) : 0
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(safeValue)
  }, [])

  const getBudgetSummaryForPo = useCallback(
    (sourceRecord: any) => {
      const poId = sourceRecord?.id
      const detail = poId ? poDetailsById[Number(poId)] : null
      const record = detail ? { ...sourceRecord, ...detail } : sourceRecord

      const candidateCodes = new Set<string>()

      const addCode = (val: any) => {
        if (val !== undefined && val !== null) {
          const str = String(val).trim()
          if (str && str !== '-' && str !== 'null' && str !== 'undefined') {
            candidateCodes.add(str)
          }
        }
      }

      const addId = (idVal: any) => {
        const idNum = Number(idVal)
        if (Number.isFinite(idNum) && idNum > 0) {
          const resolved = accountCodeById.get(idNum)
          if (resolved) {
            candidateCodes.add(resolved)
          }
        }
      }

      // Direct fields on the PO record — ONLY budget account codes (not logistics)
      addCode(record.budgetCode)
      addCode(record.budget_code)
      addCode(record.budgetAccountCode)
      addCode(record.budget_account_code)

      addId(record.budgetAccountCodeId)
      addId(record.budget_account_code_id)

      // Line items on the PO record
      const lineArrays = [
        record.quotationLines,
        record.quotation_lines,
        record.lines,
        record.items,
        record.purchaseOrderItems,
      ]

      for (const arr of lineArrays) {
        if (Array.isArray(arr)) {
          for (const line of arr) {
            // Only budget account codes from line items
            addCode(line.budgetAccountCode)
            addCode(line.budget_account_code)
            addCode(line.budgetCode)
            addCode(line.budget_code)
            addId(line.budgetAccountCodeId)
            addId(line.budget_account_code_id)
          }
        }
      }

      // Nested requisition / quotation / rfq — only budget codes
      addCode(record.rfq?.quotation?.budgetCode)
      addCode(record.rfq?.quotation?.budget_code)
      addCode(record.quotation?.budgetCode)
      addCode(record.quotation?.budget_code)
      addCode(record.rfq?.requisition?.budgetCode)
      addCode(record.rfq?.requisition?.budget_code)
      addCode(record.requisition?.budgetCode)
      addCode(record.requisition?.budget_code)

      const recordVesselId = Number(
        record.vesselId ??
        record.vessel_id ??
        record.vessel?.id ??
        record.vessel?.vesselId ??
        record.vessel?.vessel_id ??
        record.rfq?.requisition?.vesselId ??
        record.rfq?.requisition?.vessel?.id ??
        record.rfq?.requisition?.vessel_id ??
        record.requisition?.vesselId ??
        record.requisition?.vessel?.id ??
        record.requisition?.vessel_id ??
        record.rfq?.vesselId ??
        record.rfq?.vessel_id
      )

      const recordVesselName = normalizePoSearchValue(
        record.vesselName ||
        record.vessel_name ||
        record.vessel?.fleetName ||
        record.vessel?.fleet_name ||
        record.vessel?.name ||
        resolveVesselName(record)
      )

      // 1. Find all budgets matching this PO's vessel
      const vesselBudgets = budgetData.filter((b: any) => {
        const bVesselId = Number(b.vesselId ?? b.vessel_id)
        const bVesselName = normalizePoSearchValue(
          b.vessel?.fleetName ||
          b.vessel?.fleet_name ||
          b.vessel?.name ||
          b.vessel?.imoNumber ||
          String(b.vesselId || b.vessel_id || '')
        )

        const vesselMatch =
          (Number.isFinite(recordVesselId) && recordVesselId > 0 && bVesselId === recordVesselId) ||
          (recordVesselName && bVesselName && (bVesselName === recordVesselName || recordVesselName.includes(bVesselName) || bVesselName.includes(recordVesselName)))

        return vesselMatch
      })

      const candidateCodeList = Array.from(candidateCodes)

      // If PO does not have any account code, do NOT show whole vessel totals
      if (candidateCodeList.length === 0) {
        return {
          hasBudget: false,
          accountCode: '',
          budget: null as number | null,
          expenses: null as number | null,
          remaining: null as number | null,
          isOneTime: false,
        }
      }

      // 2. Find matching budget items for each candidate account code (prefer vessel-matched budgets, fallback to budgetData)
      const targetBudgetList = vesselBudgets.length > 0 ? vesselBudgets : budgetData
      const matchedBudgets: any[] = []
      const matchedCodes: string[] = []

      const normalizeClean = (str: string) =>
        str
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .replace(/[\-_]/g, ' ')
          .trim()

      for (const targetCode of candidateCodeList) {
        const normTarget = normalizeClean(targetCode)
        const matching = targetBudgetList.filter((b: any) => {
          const bAccountCode = normalizeClean(String(b.accountCodes || b.account_codes || b.accountCode || ''))
          return (
            bAccountCode === normTarget ||
            bAccountCode.startsWith(normTarget + ' ') ||
            normTarget.startsWith(bAccountCode + ' ')
          )
        })

        if (matching.length > 0) {
          const poDateRaw =
            record.deliveryDate ||
            record.delivery_date ||
            sourceRecord.deliveryDate ||
            sourceRecord.delivery_date ||
            record.poDate ||
            record.po_date ||
            sourceRecord.poDate ||
            sourceRecord.po_date ||
            record.createdAt
          const poMonth = poDateRaw ? String(poDateRaw).slice(0, 7) : null

          let matched = null
          if (poMonth) {
            // 1. Exact month match
            matched = matching.find((b: any) => {
              const bMonth = String(b.budgetMonth || b.budget_month || '').slice(0, 7)
              return bMonth === poMonth
            })

            // 2. If not found, find the latest budget that started on or before the PO month
            if (!matched) {
              const pastOrSame = matching
                .filter((b: any) => String(b.budgetMonth || b.budget_month || '').slice(0, 7) <= poMonth)
                .sort((a: any, b: any) => String(b.budgetMonth || b.budget_month || '').localeCompare(String(a.budgetMonth || a.budget_month || '')))
              if (pastOrSame.length > 0) {
                matched = pastOrSame[0]
              }
            }

            // 3. If PO month is before all existing budgets (e.g. PO is 2026-07 and first budget is 2026-08-01),
            // match the earliest available budget (which corresponds to that initial period) rather than future months!
            if (!matched) {
              const sortedAsc = [...matching].sort((a: any, b: any) => {
                const monthA = String(a.budgetMonth || a.budget_month || '')
                const monthB = String(b.budgetMonth || b.budget_month || '')
                return monthA.localeCompare(monthB)
              })
              matched = sortedAsc[0]
            }
          }

          // Fallback to latest available budget only if no PO date was available
          if (!matched) {
            const sorted = [...matching].sort((a: any, b: any) => {
              const monthA = String(a.budgetMonth || a.budget_month || '')
              const monthB = String(b.budgetMonth || b.budget_month || '')
              return monthB.localeCompare(monthA)
            })
            matched = sorted[0]
          }

          matchedBudgets.push(matched)
          matchedCodes.push(String(matched.accountCodes || matched.account_codes || matched.accountCode || targetCode).trim())
        }
      }

      if (matchedBudgets.length === 0) {
        return {
          hasBudget: false,
          accountCode: candidateCodeList.join(', '),
          budget: null as number | null,
          expenses: null as number | null,
          remaining: null as number | null,
          isOneTime: false,
        }
      }

      // If only 1 account code matched, display its specific values
      if (matchedBudgets.length === 1) {
        const latest = matchedBudgets[0]
        const totalBudget = Number(latest.totalBudget ?? latest.total_budget ?? latest.baseBudget ?? latest.base_budget ?? latest.budget ?? 0)
        const baseBudget = Number(latest.baseBudget ?? latest.base_budget ?? latest.budget ?? 0)
        const totalExpenses = Number(latest.spentAmount ?? latest.spent_amount ?? latest.spent ?? 0)
        const remainingAmount = Number(
          latest.remainingAmount ?? latest.remaining_amount ?? (totalBudget - totalExpenses)
        )

        return {
          hasBudget: true,
          accountCode: matchedCodes[0],
          budget: baseBudget || totalBudget,
          expenses: totalExpenses,
          remaining: remainingAmount,
          isOneTime: !!(latest.isOneTime || latest.is_one_time),
        }
      }

      // If multiple distinct account codes matched for this PO, aggregate ONLY those matched codes
      const totalBudget = matchedBudgets.reduce(
        (sum, m) => sum + Number(m.baseBudget ?? m.base_budget ?? m.totalBudget ?? m.total_budget ?? m.budget ?? 0),
        0
      )
      const totalExpenses = matchedBudgets.reduce(
        (sum, m) => sum + Number(m.spentAmount ?? m.spent_amount ?? m.spent ?? 0),
        0
      )
      const remainingAmount = matchedBudgets.reduce(
        (sum, m) =>
          sum +
          Number(
            m.remainingAmount ??
            m.remaining_amount ??
            (Number(m.totalBudget ?? m.baseBudget ?? 0) - Number(m.spentAmount ?? 0))
          ),
        0
      )

      return {
        hasBudget: true,
        accountCode: Array.from(new Set(matchedCodes)).join(', '),
        budget: totalBudget,
        expenses: totalExpenses,
        remaining: remainingAmount,
        isOneTime: matchedBudgets.some((m) => !!(m.isOneTime || m.is_one_time)),
      }
    },
    [accountCodeById, budgetData, poDetailsById]
  )

  useEffect(() => {
    loadPurchaseOrders()
  }, [loadPurchaseOrders])

  const stageOptions: FilterOption[] = [
    { value: '', label: 'All Stages' },
        { value: 'DRAFT', label: 'DRAFT' },
    { value: 'PENDING_OWNER_APPROVAL', label: 'Pending Owner Approval' },
    { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REWORK', label: 'Rework' },
    { value: 'SENT_TO_VENDOR', label: 'Sent to Vendor' },
    { value: 'CONFIRMED', label: 'Confirmed' },
    { value: 'CONFIRMED', label: 'Confirmed' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'INVOICE_RECEIVED', label: 'Invoice Received' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'COMPLETED', label: 'Completed' },
  ]

  const approvalPendingWithOptions: FilterOption[] = [
    { value: '', label: 'All' },
    { value: 'COMPANY', label: 'Company' },
    { value: 'VENDOR', label: 'Vendor' },
    { value: 'FINANCE', label: 'Finance' },
    { value: 'NONE', label: 'None' },
  ]

  const vesselOptions = useMemo<FilterOption[]>(() => {
    const values = Array.from(
      new Set(
        purchaseOrderData
          .map((record) => resolveVesselName(record))
          .filter(Boolean)
      )
    )
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .sort()
    return [{ value: '', label: 'All Vessels' }, ...values.map((v) => ({ value: v, label: v }))]
  }, [purchaseOrderData])

  const departmentOptions = useMemo<FilterOption[]>(
    () => [
      { value: '', label: 'All Departments' },
      ...Array.from(new Set(purchaseOrderData.map((record) => record.categoryName || record.category?.name).filter(Boolean)))
        .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
        .sort()
        .map((name) => ({ value: name as string, label: name as string })),
    ],
    [purchaseOrderData]
  )

  const filteredData = useMemo(() => {
    return purchaseOrderData.filter(record => {
      const searchNeedle = normalizePoSearchValue(searchTerm)
      const poNumber = normalizePoSearchValue(record.poNumber)
      const description = normalizePoSearchValue(record.description)
      const vendorName = normalizePoSearchValue(record.vendorName || record.vendor?.name)
      const categoryName = normalizePoSearchValue(record.categoryName || record.category?.name)
      const requisitionNo = normalizePoSearchValue(
        record.requisitionCode || record.requisitionNo || (record as any)?.rfq?.requisition?.requisition_code || (record as any)?.rfq?.requisition?.requisitionCode
      )
      const rfqNo = normalizePoSearchValue((record as any)?.rfq?.rfqNo || record.rfqNumber || (record as any)?.rfq?.requisition?.rfqNo)
      const vesselName = normalizePoSearchValue(record.vesselName || resolveVesselName(record as any))
      const supplierReference = normalizePoSearchValue(
        (record as any)?.supplierReference ||
        (record as any)?.vendorReference ||
        (record as any)?.quotation?.supplierReference
      )
      const raisedBy = normalizePoSearchValue((record as any)?.poRaisedByUser)
      const statusName = normalizePoSearchValue(record.status)
      const deliveryDate = String((record as any)?.poDate || record.deliveryDate || '')
      const deliveryDateSearch = normalizePoSearchValue(deliveryDate)

      const matchesSearch = searchNeedle === '' || [
        poNumber,
        description,
        vendorName,
        categoryName,
        requisitionNo,
        rfqNo,
        vesselName,
        supplierReference,
        raisedBy,
        statusName,
        deliveryDateSearch,
      ].some((value) => value.includes(searchNeedle))

      const matchesStatus = statusFilter === '' || record.status === statusFilter
      const requisitionTitle = String((record as any)?.rfq?.requisition?.title || '').toLowerCase()
      const requisitionName = String((record as any)?.rfq?.requisition?.name || '').toLowerCase()
      const deliveredFlag = ['DELIVERED', 'COMPLETED'].includes(record.status) ? 'YES' : 'NO'
      const pendingWith =
        record.status === 'PENDING_OWNER_APPROVAL'
          ? 'COMPANY'
          : record.status === 'PENDING_APPROVAL'
            ? 'COMPANY'
            : record.status === 'SENT_TO_VENDOR'
              ? 'VENDOR'
              : record.status === 'INVOICE_RECEIVED'
                ? 'FINANCE'
                : 'NONE'

      const matchesReference =
        referenceFilter === '' ||
        poNumber.includes(referenceFilter.toLowerCase()) ||
        requisitionNo.includes(referenceFilter.toLowerCase()) ||
        rfqNo.includes(referenceFilter.toLowerCase())

      const matchesVessel =
        vesselFilter === '' || vesselName.includes(vesselFilter.toLowerCase())

      const matchesDepartment =
        departmentFilter === '' ||
        categoryName.includes(departmentFilter.toLowerCase())

      const matchesCatalogue =
        catalogueFilter === '' ||
        categoryName.includes(catalogueFilter.toLowerCase())

      const matchesReqTitle =
        requisitionTitleFilter === '' ||
        requisitionTitle.includes(requisitionTitleFilter.toLowerCase()) ||
        requisitionName.includes(requisitionTitleFilter.toLowerCase())

      const matchesApprovalPending =
        approvalPendingWithFilter === '' || pendingWith === approvalPendingWithFilter

      const matchesDateFrom = poDateFromFilter === '' || deliveryDate >= poDateFromFilter
      const matchesDateTo = poDateToFilter === '' || deliveryDate <= poDateToFilter
      const matchesDelivered =
        deliveredToVesselFilter === '' || deliveredFlag === deliveredToVesselFilter

      return (
        matchesSearch &&
        matchesStatus &&
        matchesReference &&
        matchesVessel &&
        matchesDepartment &&
        matchesCatalogue &&
        matchesReqTitle &&
        matchesApprovalPending &&
        matchesDateFrom &&
        matchesDateTo &&
        matchesDelivered
      )
    })
  }, [
    searchTerm,
    purchaseOrderData,
    statusFilter,
    referenceFilter,
    vesselFilter,
    departmentFilter,
    catalogueFilter,
    requisitionTitleFilter,
    approvalPendingWithFilter,
    poDateFromFilter,
    poDateToFilter,
    deliveredToVesselFilter,
  ])

  const sortedData = useMemo(() => {
    let sortedRecords = [...filteredData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        let aVal: any
        let bVal: any
        const key = sortConfig.key!

        if (key === 'poNumber') {
          aVal = normalizePoSearchValue(a.poNumber)
          bVal = normalizePoSearchValue(b.poNumber)
        } else if (key === 'vendor') {
          aVal = normalizePoSearchValue(a.vendorName || a.vendor?.name)
          bVal = normalizePoSearchValue(b.vendorName || b.vendor?.name)
        } else if (key === 'deliveryDate') {
          aVal = a.deliveryDate
          bVal = b.deliveryDate
        } else if (key === 'totalAmount') {
          aVal = resolvePurchaseOrderMoneyPresentation(a, isVendorView).primaryAmount ?? 0
          bVal = resolvePurchaseOrderMoneyPresentation(b, isVendorView).primaryAmount ?? 0
        } else if (key === 'budgetAllocated') {
          const bA = getBudgetSummaryForPo(a)
          const bB = getBudgetSummaryForPo(b)
          aVal = bA.budget ?? -1
          bVal = bB.budget ?? -1
        } else if (key === 'budgetExpenses') {
          const bA = getBudgetSummaryForPo(a)
          const bB = getBudgetSummaryForPo(b)
          aVal = bA.expenses ?? -1
          bVal = bB.expenses ?? -1
        } else if (key === 'budgetRemaining') {
          const bA = getBudgetSummaryForPo(a)
          const bB = getBudgetSummaryForPo(b)
          aVal = bA.remaining ?? -Infinity
          bVal = bB.remaining ?? -Infinity
        } else {
          aVal = String((a as any)[key] || '').toLowerCase()
          bVal = String((b as any)[key] || '').toLowerCase()
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [filteredData, sortConfig])

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const filteredTotalElements = serverTotalElements || sortedData.length
  const currentRecords = sortedData
  const totalPages = serverTotalPages || Math.max(1, Math.ceil(filteredTotalElements / rowsPerPage))
  const currentRecordIdsKey = useMemo(
    () => currentRecords.map((record) => `${record.id}:${record.ownerApprovalRequired ? 'legacy' : 'policy'}`).join(','),
    [currentRecords]
  )

  useEffect(() => {
    if (listMode === 'OWNER_QUEUE') {
      setApprovalSnapshots({})
      return
    }
    void loadApprovalSnapshots(currentRecords)
  }, [currentRecordIdsKey, loadApprovalSnapshots, listMode])

  useEffect(() => {
    if (currentRecords.length > 0) {
      void loadPoDetails(currentRecords)
    }
  }, [currentRecordIdsKey, loadPoDetails, budgetData])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    setReferenceFilter('')
    setVesselFilter('')
    setDepartmentFilter('')
    setCatalogueFilter('')
    setRequisitionTitleFilter('')
    setApprovalPendingWithFilter('')
    setPoDateFromFilter('')
    setPoDateToFilter('')
    setDeliveredToVesselFilter('')
    setCurrentPage(1)
  }

  const filterByDateRange = (value?: string | null, from?: string, to?: string) => {
    const dateValue = String(value || '').slice(0, 10)
    if (from && (!dateValue || dateValue < from)) return false
    if (to && (!dateValue || dateValue > to)) return false
    return true
  }

  const normalize = (value?: string | null) => String(value || '').trim().toLowerCase()

  const loadRFQDocumentPayload = async (rfqId: number) => {
    const fullRFQ = await getRFQById(rfqId)
    const reqDetail = await getRequisitionById(fullRFQ.requisitionId)
    const quotationsPage = await getQuotationsByRFQPage(rfqId, 0, 200)
    const quotations = (quotationsPage.content || []).map((quotation: any) => ({
      ...quotation,
      lines: Array.isArray(quotation?.lines) ? quotation.lines : [],
      attachments: Array.isArray(quotation?.attachments) ? quotation.attachments : [],
    }))
    const selectedQuotations = quotations.filter(
      (quotation: any) => String(quotation?.status || '').toUpperCase() === 'SELECTED'
    )
    return {
      ...fullRFQ,
      requisition: reqDetail || (fullRFQ as any).requisition || null,
      quotations,
      selectedQuotations,
      attachments: Array.isArray(fullRFQ.attachments) ? fullRFQ.attachments : [],
    }
  }

  const handleDownloadPO = async (poId: number, options?: { silent?: boolean }) => {
    if (!canDownloadPO) {
      if (!options?.silent) toast.error('You do not have permission to download purchase orders.')
      return
    }
    try {
      const fullPo = await getPurchaseOrderDocumentById(poId)
      setPoDownloadDoc(fullPo)
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf('#po-download-host', `Purchase_Order_${fullPo.poNumber || poId}`)
      if (!options?.silent) toast.success('Purchase Order PDF downloaded')
    } catch (error) {
      console.error('Failed to download purchase order PDF', error)
      if (!options?.silent) toast.error('Failed to download purchase order PDF')
      throw error
    } finally {
      setPoDownloadDoc(null)
    }
  }

  const handleDownloadRFQ = async (rfqId: number, options?: { silent?: boolean }) => {
    if (!canDownloadRFQ) {
      if (!options?.silent) toast.error('You do not have permission to download RFQs.')
      return
    }
    try {
      const payload = await loadRFQDocumentPayload(rfqId)
      setRfqDownloadDoc(payload)
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf('#po-rfq-download-host', `RFQ_${payload?.title || rfqId}`)
      if (!options?.silent) toast.success('RFQ PDF downloaded')
    } catch (error) {
      console.error('Failed to download RFQ PDF', error)
      if (!options?.silent) toast.error('Failed to download RFQ PDF')
      throw error
    } finally {
      setRfqDownloadDoc(null)
    }
  }

  const handleDownloadRequisition = async (requisitionId: number, options?: { silent?: boolean }) => {
    if (!canDownloadRequisition) {
      if (!options?.silent) toast.error('You do not have permission to download requisitions.')
      return
    }
    try {
      const requisition = await getRequisitionById(requisitionId)
      const payload = buildRequisitionDownloadDoc(requisition)
      setRequisitionDownloadDoc(payload)
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf(
        '#po-requisition-download-host',
        `Requisition_${payload.requisition.reqNumber || requisitionId}`
      )
      if (!options?.silent) toast.success('Requisition PDF downloaded')
    } catch (error) {
      console.error('Failed to download requisition PDF', error)
      if (!options?.silent) toast.error('Failed to download requisition PDF')
      throw error
    } finally {
      setRequisitionDownloadDoc(null)
    }
  }

  const handleDownloadGoodsReceipt = async (receiptId: number, options?: { silent?: boolean }) => {
    if (!canDownloadGR) {
      if (!options?.silent) toast.error('You do not have permission to download goods receipts.')
      return
    }
    try {
      const receipt = await getGoodsReceiptById(receiptId)
      setGoodsReceiptDownloadDoc({ receipt })
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf('#po-gr-download-host', `Goods_Receipt_${receipt.id}`)
      if (!options?.silent) toast.success('Goods Receipt PDF downloaded')
    } catch (error) {
      console.error('Failed to download goods receipt PDF', error)
      if (!options?.silent) toast.error('Failed to download goods receipt PDF')
      throw error
    } finally {
      setGoodsReceiptDownloadDoc(null)
    }
  }

  const handleDownloadInvoice = async (invoiceId: number, options?: { silent?: boolean }) => {
    if (!canDownloadInvoice) {
      if (!options?.silent) toast.error('You do not have permission to download invoices.')
      return
    }
    try {
      const invoice = await getInvoiceById(invoiceId)
      const po = invoice.purchaseOrderId ? await getPurchaseOrderById(invoice.purchaseOrderId).catch(() => null) : null
      const goodsReceipt = invoice.goodsReceiptId ? await getGoodsReceiptById(invoice.goodsReceiptId).catch(() => null) : null
      const requisitionId = (po as any)?.rfq?.requisition?.id || null
      const requisition = requisitionId ? await getRequisitionById(requisitionId).catch(() => null) : null
      setInvoiceDownloadDoc({ invoice, purchaseOrder: po, goodsReceipt, requisition })
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf('#po-invoice-download-host', `Invoice_${invoice.invoiceNumber || invoice.id}`)
      if (!options?.silent) toast.success('Invoice PDF downloaded')
    } catch (error) {
      console.error('Failed to download invoice PDF', error)
      if (!options?.silent) toast.error('Failed to download invoice PDF')
      throw error
    } finally {
      setInvoiceDownloadDoc(null)
    }
  }

  const exportPurchaseOrdersToPDF = () => {
    if (!canExportPO) {
      toast.error('You do not have permission to export purchase orders.')
      return
    }
    setBulkType('PO')
    setIsBulkModalOpen(true)
  }

  const bulkDownload = async () => {
    if (!canDownloadType(bulkType)) {
      toast.error('You do not have download permission for the selected document type.')
      return
    }
    setIsBulkDownloading(true)
    try {
      if (bulkType === 'PO') {
        const filtered = sortedData.filter((po) => {
          const matchesDate = filterByDateRange((po as any)?.poDate || po.deliveryDate || '', bulkFrom, bulkTo)
          const matchesCompany = !bulkCompany || normalize((po as any)?.rfq?.requisition?.category?.cgaid?.name) === normalize(bulkCompany)
          const matchesVessel = !bulkVessel || normalize(resolveVesselName(po)) === normalize(bulkVessel)
          const matchesVendor = !bulkVendor || normalize(po.vendor?.name) === normalize(bulkVendor)
          const matchesStatus = !bulkStatus || normalize(po.status) === normalize(bulkStatus)
          return matchesDate && matchesCompany && matchesVessel && matchesVendor && matchesStatus
        })
        if (!filtered.length) {
          toast.info('No purchase orders found for selected filters')
          return
        }
        for (const po of filtered) {
          await handleDownloadPO(po.id, { silent: true })
        }
        toast.success(`${filtered.length} purchase orders exported`)
      } else if (bulkType === 'RFQ') {
        let page = 0
        const pageSize = 200
        const rfqs: any[] = []
        while (true) {
          const { content, last } = await getRFQsPage({ page, size: pageSize })
          rfqs.push(...(content || []))
          if (last || (content || []).length === 0) break
          page += 1
        }
        const filtered: number[] = []
        for (const rfq of rfqs) {
          if (!filterByDateRange(rfq?.responseDueDate || '', bulkFrom, bulkTo)) continue
          if (bulkStatus && normalize(rfq?.status) !== normalize(bulkStatus)) continue
          const payload = await loadRFQDocumentPayload(rfq.id)
          const companyName = payload?.requisition?.category?.cgaid?.name || ''
          const vesselName = resolveVesselName(payload?.requisition)
          const vendorNames = (payload?.selectedQuotations || payload?.quotations || [])
            .map((quotation: any) => quotation?.vendor?.name)
            .filter(Boolean)
            .map((name: string) => normalize(name))
          if (bulkCompany && normalize(companyName) !== normalize(bulkCompany)) continue
          if (bulkVessel && normalize(vesselName) !== normalize(bulkVessel)) continue
          if (bulkVendor && !vendorNames.includes(normalize(bulkVendor))) continue
          filtered.push(rfq.id)
        }
        if (!filtered.length) {
          toast.info('No RFQs found for selected filters')
          return
        }
        for (const rfqId of filtered) {
          await handleDownloadRFQ(rfqId, { silent: true })
        }
        toast.success(`${filtered.length} RFQs exported`)
      } else if (bulkType === 'REQUISITION') {
        const reqPage = await getRequisitions(0, 500)
        const requisitions = (reqPage?.content || []) as any[]
        const filtered = requisitions.filter((requisition) => {
          const matchesDate = filterByDateRange(
            requisition?.requiredDeliveryDate || requisition?.submittedAt || requisition?.createdAt || '',
            bulkFrom,
            bulkTo
          )
          const matchesCompany =
            !bulkCompany || normalize(requisition?.category?.cgaid?.name) === normalize(bulkCompany)
          const matchesVessel =
            !bulkVessel ||
            normalize(resolveVesselName(requisition)) === normalize(bulkVessel)
          const matchesStatus = !bulkStatus || normalize(requisition?.status) === normalize(bulkStatus)
          return matchesDate && matchesCompany && matchesVessel && matchesStatus
        })
        if (!filtered.length) {
          toast.info('No requisitions found for selected filters')
          return
        }
        for (const requisition of filtered) {
          await handleDownloadRequisition(Number(requisition.id), { silent: true })
        }
        toast.success(`${filtered.length} requisitions exported`)
      } else if (bulkType === 'GR') {
        const receipts = await getGoodsReceipts()
        const filtered: number[] = []
        for (const receipt of receipts || []) {
          if (!filterByDateRange(receipt.receivedDate || '', bulkFrom, bulkTo)) continue
          if (bulkStatus && normalize(receipt.status) !== normalize(bulkStatus)) continue
          const po = await getPurchaseOrderById(receipt.purchaseOrderId).catch(() => null)
          const companyName = (po as any)?.rfq?.requisition?.category?.cgaid?.name || ''
          const vesselName = resolveVesselName(po)
          const vendorName = po?.vendor?.name || ''
          if (bulkCompany && normalize(companyName) !== normalize(bulkCompany)) continue
          if (bulkVessel && normalize(vesselName) !== normalize(bulkVessel)) continue
          if (bulkVendor && normalize(vendorName) !== normalize(bulkVendor)) continue
          filtered.push(receipt.id)
        }
        if (!filtered.length) {
          toast.info('No goods receipts found for selected filters')
          return
        }
        for (const receiptId of filtered) {
          await handleDownloadGoodsReceipt(receiptId, { silent: true })
        }
        toast.success(`${filtered.length} goods receipts exported`)
      } else if (bulkType === 'INVOICE') {
        const invoices = await getInvoices()
        const filtered: number[] = []
        for (const invoice of invoices || []) {
          if (!filterByDateRange(invoice.invoiceDate || '', bulkFrom, bulkTo)) continue
          if (bulkStatus && normalize(invoice.status) !== normalize(bulkStatus)) continue
          const po = invoice.purchaseOrderId ? await getPurchaseOrderById(invoice.purchaseOrderId).catch(() => null) : null
          const companyName = (po as any)?.rfq?.requisition?.category?.cgaid?.name || ''
          const vesselName = resolveVesselName(po)
          const vendorName = po?.vendor?.name || ''
          if (bulkCompany && normalize(companyName) !== normalize(bulkCompany)) continue
          if (bulkVessel && normalize(vesselName) !== normalize(bulkVessel)) continue
          if (bulkVendor && normalize(vendorName) !== normalize(bulkVendor)) continue
          filtered.push(invoice.id)
        }
        if (!filtered.length) {
          toast.info('No invoices found for selected filters')
          return
        }
        for (const invoiceId of filtered) {
          await handleDownloadInvoice(invoiceId, { silent: true })
        }
        toast.success(`${filtered.length} invoices exported`)
      }
      setIsBulkModalOpen(false)
    } catch (err) {
      console.error('Bulk download failed', err)
      toast.error('Bulk download failed')
    } finally {
      setIsBulkDownloading(false)
    }
  }

  const handleOpenQuotationManagement = () => {
    setIsModalVisible(false)
    navigate('../quotation-management')
  }

  const handleOpenDirectPOCreate = () => {
    setIsModalVisible(false)
    setEditingDirectPO(null)
    setIsDirectPoModalVisible(true)
  }

  const handleViewPO = async (record: PurchaseOrder) => {
    try {
      const fullPo = await getPurchaseOrderById(record.id)
      setSelectedPO(fullPo)
      setIsViewModalVisible(true)
    } catch (error: any) {
      console.error('Error loading purchase order details:', error)
      toast.error('Failed to load purchase order details')
    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const poId = Number(urlParams.get('poId') || 0)
    if (!Number.isFinite(poId) || poId <= 0 || openedPoIdRef.current === poId) {
      return
    }
    openedPoIdRef.current = poId
    void handleViewPO({id: poId} as PurchaseOrder)
  }, [currentPage, rowsPerPage])

  useEffect(() => {
    if (!isViewModalVisible && openedPoIdRef.current != null) {
      const params = new URLSearchParams(window.location.search)
      if (params.get('poId')) {
        params.delete('poId')
        navigate({search: params.toString() ? `?${params.toString()}` : ''}, {replace: true})
      }
      openedPoIdRef.current = null
    }
  }, [isViewModalVisible, navigate])

  const handleEditDirectPO = async (record: PurchaseOrder) => {
    try {
      const fullPo = await getPurchaseOrderById(record.id)
      if (!isDirectPurchaseOrder(fullPo)) {
        toast.error('Only Direct PO records can be edited from this flow')
        return
      }
      setEditingDirectPO(fullPo)
      setIsDirectPoModalVisible(true)
    } catch (error: any) {
      console.error('Error loading direct purchase order for edit:', error)
      toast.error(error?.message || 'Failed to load Direct PO for edit')
    }
  }

  const handleEditCompanyPO = async (record: PurchaseOrder) => {
    try {
      setEditingCompanyPO(await getPurchaseOrderById(record.id))
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load purchase order for edit')
    }
  }

  const handleSendPO = async (id: number) => {
    if (!window.confirm('Are you sure you want to send this Purchase Order to the vendor?')) return

    try {
      await sendPurchaseOrder(id)
      toast.success('Purchase Order sent successfully')
      // Refresh the list
      await loadPurchaseOrders()
    } catch (error: any) {
      console.error('Error sending purchase order:', error)
      toast.error('Failed to send purchase order')
    }
  }

  const handleSubmitPOForApproval = async (id: number) => {
    if (!window.confirm('Submit this purchase order for approval?')) return
    try {
      await submitPurchaseOrderForApproval(id)
      toast.success('Purchase order submitted for approval')
      await loadPurchaseOrders()
    } catch (error: any) {
      console.error('Error submitting purchase order for approval:', error)
      toast.error(error?.message || 'Failed to submit purchase order for approval')
    }
  }

  const handleApprovalAction = async (id: number, action: 'APPROVE' | 'REJECT') => {
    const promptResult = action === 'REJECT'
      ? window.prompt('Reason for rejecting this purchase order:')
      : window.prompt('Approval remark (optional):')
    if (promptResult === null) return

    const remark = promptResult.trim()
    if (action === 'REJECT' && !remark) return

    try {
      const record = purchaseOrderData.find((row) => Number(row.id) === id)
      if (String(record?.status || '').toUpperCase() === 'DRAFT') {
        if (action === 'APPROVE') {
          await ownerApprovePurchaseOrder(id, remark || undefined)
        } else {
          await ownerRejectPurchaseOrder(id, remark)
        }
      } else {
        const instance = await getApprovalInstanceByDocument('PURCHASE_ORDER', id)
        if (!instance?.id) {
          toast.error('No active approval instance found for this purchase order')
          return
        }
        await actOnApprovalInstance(instance.id, action, remark || undefined)
      }
      toast.success(action === 'APPROVE' ? 'Purchase order approved' : 'Purchase order rejected')
      await loadPurchaseOrders()
    } catch (error: any) {
      toast.error(error?.message || `Failed to ${action.toLowerCase()} purchase order`)
    }
  }

  const openOwnerActionDialog = (
    action: 'APPROVE' | 'REJECT' | 'REWORK',
    po: PurchaseOrder
  ) => {
    setOwnerActionDialog({ action, po })
    setOwnerActionRemark('')
  }

  const closeOwnerActionDialog = () => {
    setOwnerActionDialog(null)
    setOwnerActionRemark('')
  }

  const submitOwnerAction = async () => {
    if (!ownerActionDialog) return
    const remark = ownerActionRemark.trim()
    if ((ownerActionDialog.action === 'REJECT' || ownerActionDialog.action === 'REWORK') && !remark) {
      toast.warning('Remark is required for this action')
      return
    }
    try {
      setOwnerActionSubmitting(true)
      if (ownerActionDialog.action === 'APPROVE') {
        await ownerApprovePurchaseOrder(ownerActionDialog.po.id, remark || undefined)
        toast.success('PO approved by owner')
      } else if (ownerActionDialog.action === 'REJECT') {
        await ownerRejectPurchaseOrder(ownerActionDialog.po.id, remark)
        toast.success('PO rejected by owner')
      } else {
        await ownerReworkPurchaseOrder(ownerActionDialog.po.id, remark)
        toast.success('PO sent for rework')
      }

      await loadPurchaseOrders()
      closeOwnerActionDialog()
    } catch (error: any) {
      console.error('Error performing owner action on PO:', error)
      toast.error('Failed to update purchase order')
    } finally {
      setOwnerActionSubmitting(false)
    }
  }

  const handleCreateGoodsReceipt = (purchaseOrderId: number) => {
    if (!canCreateGR) {
      toast.error('You do not have goods receipt create access.')
      return
    }
    setSelectedPOForReceipt(purchaseOrderId)
    setIsGoodsReceiptModalVisible(true)
  }

  const handleGoodsReceiptSubmit = () => {
    // Refresh the purchase orders list if needed
    setIsGoodsReceiptModalVisible(false)
    setSelectedPOForReceipt(null)
  }

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { cls: string; text: string } } = {
      'DRAFT': { cls: 'bg-light-info text-info', text: 'Draft' },
      'PENDING_OWNER_APPROVAL': { cls: 'bg-warning text-dark', text: 'Pending Owner Approval' },
      'SENT_TO_VENDOR': { cls: 'bg-primary text-white', text: 'Sent to Vendor' },
      'PENDING_APPROVAL': { cls: 'bg-warning text-dark', text: 'Pending Approval' },
      'APPROVED': { cls: 'bg-success text-white', text: 'Approved' },
      'REWORK': { cls: 'bg-light-warning text-warning', text: 'Rework' },
      'REJECTED': { cls: 'bg-danger text-white', text: 'Rejected' },
      'INVOICE_RECEIVED': { cls: 'bg-dark text-white', text: 'Invoice Received' },
      'PARTIALLY_DELIVERED': { cls: 'bg-light-warning text-warning', text: 'Partially Delivered' },
      'DELIVERED': { cls: 'bg-success text-white', text: 'Delivered' },
      'CONFIRMED': { cls: 'bg-info text-white', text: 'Confirmed' },
      'COMPLETED': { cls: 'bg-info text-white', text: 'Completed' }
    }

    const statusInfo = statusMap[status] || { cls: 'bg-light-dark text-dark', text: status }
    return <span className={`badge ${statusInfo.cls}`}>{statusInfo.text}</span>
  }

  if (procurementPermissionsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading procurement permissions...</span>
        </div>
      </div>
    )
  }

  if (!canViewPO) {
    return <div className='alert alert-warning m-6'>You do not have purchase order view access.</div>
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between align-items-center bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Purchase Orders</h3>
                <p className='text-muted mb-0'>
                  {listMode === 'OWNER_QUEUE'
                    ? 'Owner approval queue for pending purchase orders'
                    : 'Create and manage purchase orders'}
                </p>
              </div>
              <div className='card-toolbar d-flex gap-2'>
                {canAccessOwnerQueue && (
                  <>
                    <button
                      type='button'
                      className={`btn btn-sm ${listMode === 'ALL' ? 'btn-primary' : 'btn-light-primary'}`}
                      onClick={() => setListMode('ALL')}
                    >
                      All POs
                    </button>
                    <button
                      type='button'
                      className={`btn btn-sm ${listMode === 'OWNER_QUEUE' ? 'btn-primary' : 'btn-light-primary'}`}
                      onClick={() => setListMode('OWNER_QUEUE')}
                    >
                      Owner Queue
                    </button>
                  </>
                )}
                {canDownloadPO && (
                  <button
                    type='button'
                    className='btn btn_primary'
                    style={{
                      backgroundColor: '',
                      color: 'white',
                      border: 'none'
                    }}
                    onClick={exportPurchaseOrdersToPDF}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Export
                  </button>
                )}
                {canDownloadAny && (
                  <button
                    type='button'
                    className='btn btn-light btn-sm ms-2'
                    onClick={() => setIsBulkModalOpen(true)}
                  >
                    <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-2' />
                    Bulk Download
                  </button>
                )}
                {canCreatePO && (
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => setIsModalVisible(true)}
                    disabled={listMode === 'OWNER_QUEUE'}
                  >
                    <span className='me-2'>+</span>
                    Create Purchase Order
                  </button>
                )}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <ProcurementFilterPanel
                searchValue={searchTerm}
                onSearchChange={(value) => {
                  setSearchTerm(value)
                  setCurrentPage(1)
                }}
                searchPlaceholder='Search purchase orders...'
                referenceValue={referenceFilter}
                onReferenceChange={(value) => {
                  setReferenceFilter(value)
                  setCurrentPage(1)
                }}
                vesselValue={vesselFilter}
                onVesselChange={(value) => {
                  setVesselFilter(value)
                  setCurrentPage(1)
                }}
                vesselOptions={vesselOptions}
                departmentValue={departmentFilter}
                onDepartmentChange={(value) => {
                  setDepartmentFilter(value)
                  setCurrentPage(1)
                }}
                departmentOptions={departmentOptions}
                catalogueValue={catalogueFilter}
                onCatalogueChange={(value) => {
                  setCatalogueFilter(value)
                  setCurrentPage(1)
                }}
                catalogueOptions={departmentOptions}
                requisitionTitleValue={requisitionTitleFilter}
                onRequisitionTitleChange={(value) => {
                  setRequisitionTitleFilter(value)
                  setCurrentPage(1)
                }}
                approvalPendingWithValue={approvalPendingWithFilter}
                onApprovalPendingWithChange={(value) => {
                  setApprovalPendingWithFilter(value)
                  setCurrentPage(1)
                }}
                approvalPendingWithOptions={approvalPendingWithOptions}
                poDateFrom={poDateFromFilter}
                onPoDateFromChange={(value) => {
                  setPoDateFromFilter(value)
                  setCurrentPage(1)
                }}
                poDateTo={poDateToFilter}
                onPoDateToChange={(value) => {
                  setPoDateToFilter(value)
                  setCurrentPage(1)
                }}
                deliveredToVesselValue={deliveredToVesselFilter}
                onDeliveredToVesselChange={(value) => {
                  setDeliveredToVesselFilter(value)
                  setCurrentPage(1)
                }}
                stageValue={statusFilter}
                onStageChange={(value) => {
                  setStatusFilter(value)
                  setCurrentPage(1)
                }}
                stageOptions={stageOptions}
                additionalOpen={additionalFiltersOpen}
                onToggleAdditional={() => setAdditionalFiltersOpen((prev) => !prev)}
                onReset={resetFilters}
              />

              <div className='mb-8'>
                <h4 className='fw-bold text-primary mb-4'>Purchase Orders ({filteredTotalElements})</h4>
                {loading ? (
                  <div className='text-center py-5'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className='alert alert-danger' role='alert'>
                    {error}
                  </div>
                ) : (
                  <div className='report-table table-responsive'>
                    <div style={{ overflowX: 'auto' }}>
                      <table className='table table-bordered align-middle procurement-list-table'>
                        <thead className='table-header text-start'>
                          <tr>
                            <th onClick={() => handleSort('poNumber')} className='cursor-pointer align-middle'>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>PO Number</span>
                                <KTSVG
                                  path={`/media/map/sort-col-${sortConfig.key === 'poNumber' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </th>
                            <th className='align-middle' style={{ color: '#3F4254', fontWeight: 600 }}>Requisition Number</th>
                            <th onClick={() => handleSort('vendor')} className='cursor-pointer align-middle'>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>Vendor</span>
                                <KTSVG
                                  path={`/media/map/sort-col-${sortConfig.key === 'vendor' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </th>
                            <th onClick={() => handleSort('deliveryDate')} className='cursor-pointer align-middle'>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>Delivery Date</span>
                                <KTSVG
                                  path={`/media/map/sort-col-${sortConfig.key === 'deliveryDate' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </th>
                            <th onClick={() => handleSort('totalAmount')} className='cursor-pointer align-middle'>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>Total Amount</span>
                                <KTSVG
                                  path={`/media/map/sort-col-${sortConfig.key === 'totalAmount' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </th>
                            <th className='align-middle' style={{ color: '#3F4254', fontWeight: 600 }}>Logistics Total</th>
                            <th onClick={() => handleSort('budgetAllocated')} className='cursor-pointer align-middle text-end'>
                              <div className='d-flex align-items-center justify-content-end'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>Budget</span>
                                <KTSVG
                                  path={`/media/map/sort-col-${sortConfig.key === 'budgetAllocated' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </th>
                            <th onClick={() => handleSort('budgetExpenses')} className='cursor-pointer align-middle text-end'>
                              <div className='d-flex align-items-center justify-content-end'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>Expenses</span>
                                <KTSVG
                                  path={`/media/map/sort-col-${sortConfig.key === 'budgetExpenses' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </th>
                            <th onClick={() => handleSort('budgetRemaining')} className='cursor-pointer align-middle text-end'>
                              <div className='d-flex align-items-center justify-content-end'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>Remaining</span>
                                <KTSVG
                                  path={`/media/map/sort-col-${sortConfig.key === 'budgetRemaining' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                  className='svg-icon ms-2 custom-sort-icon'
                                />
                              </div>
                            </th>
                            <th className='align-middle' style={{ color: '#3F4254', fontWeight: 600 }}>Raised By</th>
                            <th className='align-middle' style={{ color: '#3F4254', fontWeight: 600 }}>Status</th>
                            <th className='align-middle text-center procurement-actions-cell' style={{ color: '#3F4254', fontWeight: 600 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody className='table-body text-start'>
                          {currentRecords.length === 0 ? (
                            <tr>
                              <td colSpan={12} className='text-center text-muted py-5'>
                                No purchase orders found for the selected criteria.
                              </td>
                            </tr>
                          ) : (
                            currentRecords.map((record) => (
                              <tr key={record.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                <td className='text-primary fw-semibold fs-6' style={{ padding: '12px 16px' }}>
                                  <button
                                    type='button'
                                    className='btn btn-link p-0 m-0 text-primary text-hover-primary'
                                    onClick={() => handleViewPO(record)}
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {record.poNumber}
                                  </button>
                                  {isDirectPurchaseOrder(record) && (
                                    <div className='mt-1'>
                                      <span className='badge bg-light-info text-info'>Direct PO</span>
                                    </div>
                                  )}
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  <div>{record.requisitionCode || record.requisitionNo || (record as any)?.rfq?.requisition?.requisition_code || (record as any)?.rfq?.requisition?.requisitionCode || '-'}</div>
                                  <small className='text-muted'>
                                    {record.requisitionTitle || (record as any)?.rfq?.requisition?.title || (record as any)?.rfq?.requisition?.name || '-'}
                                  </small>
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.vendorName || record.vendor?.name || '-'}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.deliveryDate}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {(() => {
                                    const money = resolvePurchaseOrderMoneyPresentation(record, isVendorView)
                                    return (
                                      <MoneyDisplay
                                        compact
                                        primaryAmount={money.primaryAmount}
                                        primaryCurrency={money.primaryCurrency}
                                        secondaryAmount={money.secondaryAmount}
                                        secondaryCurrency={money.secondaryCurrency}
                                        exchangeRateDisplay={money.auxiliaryText}
                                        sameCurrency={money.sameCurrency}
                                      />
                                    )
                                  })()}
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  <MoneyDisplay
                                    compact
                                    primaryAmount={record.logisticsTotal ?? 0}
                                    primaryCurrency={
                                      record.companyCurrencyCode ||
                                      record.quotationCurrency ||
                                      record.currency ||
                                      undefined
                                    }
                                    sameCurrency
                                  />
                                </td>
                                {(() => {
                                  const bSummary = getBudgetSummaryForPo(record)
                                  if (!bSummary.hasBudget) {
                                    return (
                                      <>
                                        <td className='text-muted fs-7 text-end align-middle' style={{ padding: '12px 16px' }}>
                                          {bSummary.accountCode ? (
                                            <div>
                                              <span>-</span>
                                              <small className='text-muted d-block fs-8' title={`Account Code: ${bSummary.accountCode}`}>
                                                {bSummary.accountCode}
                                              </small>
                                            </div>
                                          ) : (
                                            '-'
                                          )}
                                        </td>
                                        <td className='text-muted fs-7 text-end align-middle' style={{ padding: '12px 16px' }}>-</td>
                                        <td className='text-muted fs-7 text-end align-middle' style={{ padding: '12px 16px' }}>-</td>
                                      </>
                                    )
                                  }

                                  return (
                                    <>
                                      <td className='text-dark fs-6 text-end align-middle' style={{ padding: '12px 16px' }}>
                                        <div className='fw-semibold text-slate-700 font-monospace'>{formatBudgetCurrency(bSummary.budget)}</div>
                                        <small className='text-muted d-block fs-8' title={`Account Code: ${bSummary.accountCode}`}>
                                          {bSummary.accountCode}
                                        </small>
                                      </td>
                                      <td className='fs-6 text-end align-middle' style={{ padding: '12px 16px' }}>
                                        <div className='fw-semibold text-danger font-monospace'>{formatBudgetCurrency(bSummary.expenses)}</div>
                                      </td>
                                      <td className='fs-6 text-end align-middle' style={{ padding: '12px 16px' }}>
                                        <div className={`fw-bold font-monospace ${Number(bSummary.remaining) < 0 ? 'text-danger' : 'text-success'}`}>
                                          {formatBudgetCurrency(bSummary.remaining)}
                                        </div>
                                      </td>
                                    </>
                                  )
                                })()}
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {(record as any).poRaisedByUser || '-'}
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  {getStatusBadge(record.status)}
                                  {String(record.vendorLogisticsChangeStatus || '').toUpperCase() === 'PENDING' && (
                                    <div className='mt-1'>
                                      <span className='badge bg-light-warning text-warning'>Logistics Pending Review</span>
                                    </div>
                                  )}
                                </td>
                                <td className='text-center procurement-actions-cell' style={{ padding: '12px 16px' }}>
                                  <ProcurementActionGrid>
                                    {canReviewVendorLogistics &&
                                      String(record.vendorLogisticsChangeStatus || '').toUpperCase() === 'PENDING' && (
                                      <div className='procurement-action-subgrid d-flex flex-column align-items-start'>
                                        <button
                                          className='btn btn-sm btn-success px-2'
                                          disabled={vendorLogisticsActionId === Number(record.id)}
                                          onClick={() => void handleVendorLogisticsReview(record, 'accept')}
                                          style={{fontSize: '12px'}}
                                        >
                                          Approve Logistics
                                        </button>
                                        <button
                                          className='btn btn-sm btn-danger px-2'
                                          disabled={vendorLogisticsActionId === Number(record.id)}
                                          onClick={() => void handleVendorLogisticsReview(record, 'reject')}
                                          style={{fontSize: '12px'}}
                                        >
                                          Reject Logistics
                                        </button>
                                      </div>
                                    )}
                                    {record.ownerApprovalRequired ? (
                                      <>
                                        {canRunOwnerQueueActions && record.status === 'PENDING_OWNER_APPROVAL' && (
                                          <>
                                            <button
                                              className='btn btn-sm btn-success px-2'
                                              title='Owner Approve'
                                              onClick={() => openOwnerActionDialog('APPROVE', record)}
                                              style={{ fontSize: '12px' }}
                                            >
                                              Approve
                                            </button>

                                            <button
                                              className='btn btn-sm btn-warning px-2'
                                              title='Owner Rework'
                                              onClick={() => openOwnerActionDialog('REWORK', record)}
                                              style={{ fontSize: '12px' }}
                                            >
                                              Rework
                                            </button>

                                            <button
                                              className='btn btn-sm btn-danger px-2'
                                              title='Owner Reject'
                                              onClick={() => openOwnerActionDialog('REJECT', record)}
                                              style={{ fontSize: '12px' }}
                                            >
                                              Reject
                                            </button>
                                          </>
                                        )}

                                        {canShowSendToVendorAction(record, null) &&
                                          canSendPOPermission && (
                                            <button
                                              className='btn btn-sm btn-primary px-2'
                                              title='Send to Vendor'
                                              onClick={() => handleSendPO(record.id)}
                                              style={{ fontSize: '12px' }}
                                            >
                                              Send
                                            </button>
                                          )}
                                      </>
                                    ) : (
                                      <>
                                        {approvalSnapshots[Number(record.id)]?.availability?.canSubmit &&
                                          canSubmitPOApprovalPermission && (
                                          <div className='procurement-action-subgrid d-flex flex-column align-items-start'>
                                            <button
                                              className='btn btn-sm btn-warning px-2'
                                              title='Submit for Approval'
                                              onClick={() => handleSubmitPOForApproval(record.id)}
                                              style={{ fontSize: '12px' }}
                                            >
                                              Submit
                                            </button>
                                          </div>
                                        )}
                                        {['DRAFT', 'PENDING_OWNER_APPROVAL'].includes(String(record.status || '').toUpperCase()) &&
                                          approvalSnapshots[Number(record.id)]?.availability?.canApprove &&
                                          canApprovePOPermission && (
                                          <button
                                            className='btn btn-sm btn-success px-2'
                                            title='Approve Purchase Order'
                                            onClick={() => void handleApprovalAction(record.id, 'APPROVE')}
                                            style={{fontSize: '12px'}}
                                          >
                                            Approve
                                          </button>
                                        )}
                                        {['DRAFT', 'PENDING_OWNER_APPROVAL'].includes(String(record.status || '').toUpperCase()) &&
                                          approvalSnapshots[Number(record.id)]?.availability?.canReject &&
                                          canRejectPOPermission && (
                                          <button
                                            className='btn btn-sm btn-danger px-2'
                                            title='Reject Purchase Order'
                                            onClick={() => void handleApprovalAction(record.id, 'REJECT')}
                                            style={{fontSize: '12px'}}
                                          >
                                            Reject
                                          </button>
                                        )}
                                        {canShowSendToVendorAction(
                                          record,
                                          approvalSnapshots[Number(record.id)] ?? null
                                        ) &&
                                          canSendPOPermission && (
                                            <button
                                              className='btn btn-sm btn-primary px-2'
                                              title='Send to Vendor'
                                              onClick={() => handleSendPO(record.id)}
                                              style={{ fontSize: '12px' }}
                                            >
                                              Send
                                            </button>
                                          )}
                                      </>
                                    )}
                                    <button
                                      className='btn btn-sm px-0 procurement-action-icon'
                                      title='View Details'
                                      onClick={() => handleViewPO(record)}
                                    >
                                      <KTSVG path='/media/map/ph_eye.svg' />
                                    </button>
                                    {isDirectPurchaseOrder(record) &&
                                      canUpdatePO &&
                                      ['DRAFT', 'PENDING_OWNER_APPROVAL'].includes(
                                        String(record.status || '').toUpperCase()
                                      ) && (
                                        <button
                                          className='btn btn-sm btn-light-primary px-2'
                                          title='Edit Direct PO'
                                          onClick={() => handleEditDirectPO(record)}
                                          style={{ fontSize: '12px' }}
                                        >
                                          Edit Direct PO
                                        </button>
                                      )}
                                    {!isDirectPurchaseOrder(record) &&
                                      !isVendorView &&
                                      canUpdatePO &&
                                      ['DRAFT', 'SENT_TO_VENDOR'].includes(String(record.status || '').toUpperCase()) && (
                                        <button
                                          className='btn btn-sm btn-light-primary px-2'
                                          title='Edit PO lines'
                                          onClick={() => void handleEditCompanyPO(record)}
                                          style={{fontSize: '12px'}}
                                        >
                                          Edit lines
                                        </button>
                                      )}
                                    {canDownloadPO && (
                                      <button
                                        className='btn btn-sm btn-primary px-2'
                                        title='Download PDF'
                                        onClick={() => {
                                          void handleDownloadPO(record.id)
                                        }}
                                        style={{ fontSize: '12px' }}
                                      >
                                        Download
                                      </button>
                                    )}
                                  </ProcurementActionGrid>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div
                      className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                      style={{
                        position: 'static',
                        bottom: 0,
                        backgroundColor: '#fff',
                        zIndex: 10,
                        borderTop: '1px solid #dee2e6',
                        marginTop: 'auto'
                      }}
                    >
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Rows per page</span>
                        <SearchableSelect
                          className='form-select'
                          style={{
                            borderRadius: '20px',
                            width: '70px',
                            border: '1px solid #dee2e6',
                            fontSize: '14px',
                            padding: '4px 8px'
                          }}
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </SearchableSelect>
                      </div>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                          Showing <strong>{currentRecords.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}-{Math.min(currentPage * rowsPerPage, filteredTotalElements)}</strong> of <strong>{filteredTotalElements}</strong>
                        </span>

                        <nav>
                          <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px'
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                ‹
                              </button>
                            </li>

                            {(() => {
                              const pages = []
                              const showPages = 5
                              let startPage = Math.max(1, currentPage - 2)
                              let endPage = Math.min(totalPages, startPage + showPages - 1)

                              if (endPage - startPage < showPages - 1) {
                                startPage = Math.max(1, endPage - showPages + 1)
                              }

                              if (startPage > 1) {
                                pages.push(
                                  <li key={1} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handlePageChange(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                )

                                if (startPage > 2) {
                                  pages.push(
                                    <li key='ellipsis1' className='page-item disabled'>
                                      <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                    </li>
                                  )
                                }
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        boxShadow: 'none'
                                      }}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  </li>
                                )
                              }

                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <li key='ellipsis2' className='page-item disabled'>
                                      <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                    </li>
                                  )
                                }

                                pages.push(
                                  <li key={totalPages} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handlePageChange(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  </li>
                                )
                              }

                              return pages
                            })()}

                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px'
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                              >
                                ›
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Purchase Order Modal */}
      <ViewPurchaseOrderModal
        visible={isViewModalVisible}
        onClose={() => {
          setIsViewModalVisible(false)
          setSelectedPO(null)
        }}
        purchaseOrder={selectedPO}
        onDownload={
          canDownloadPO
            ? () => {
              if (!selectedPO) return
              void handleDownloadPO(selectedPO.id)
            }
            : undefined
        }
        onApprovalChanged={() => void loadPurchaseOrders()}
        canReviewVendorLogistics={canReviewVendorLogistics}
        onReviewVendorLogistics={(action) => {
          if (!selectedPO) return
          return handleVendorLogisticsReview(selectedPO, action).then(async () => {
            const refreshed = await getPurchaseOrderById(selectedPO.id)
            setSelectedPO(refreshed)
          })
        }}
      />

      {/* Create Purchase Order Modal */}
      {canCreatePO && (
        <CreatePurchaseOrderModal
          visible={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onOpenQuotationManagement={handleOpenQuotationManagement}
          canCreateDirectPO={canCreateDirectPO}
          onCreateDirectPO={handleOpenDirectPOCreate}
        />
      )}

      <DirectPurchaseOrderModal
        visible={isDirectPoModalVisible}
        mode={editingDirectPO ? 'edit' : 'create'}
        purchaseOrder={editingDirectPO}
        onClose={() => {
          setIsDirectPoModalVisible(false)
          setEditingDirectPO(null)
        }}
        onSuccess={async () => {
          await loadPurchaseOrders()
        }}
      />

      <CompanyPurchaseOrderLineEditModal
        visible={editingCompanyPO != null}
        purchaseOrder={editingCompanyPO}
        onClose={() => setEditingCompanyPO(null)}
        onSuccess={async () => {
          await loadPurchaseOrders()
          if (editingCompanyPO) setEditingCompanyPO(await getPurchaseOrderById(editingCompanyPO.id))
        }}
      />

      {/* Create Goods Receipt Modal */}
      <CreateReceiptModal
        visible={isGoodsReceiptModalVisible}
        onClose={() => {
          setIsGoodsReceiptModalVisible(false)
          setSelectedPOForReceipt(null)
        }}
        onSubmit={handleGoodsReceiptSubmit}
        purchaseOrderId={selectedPOForReceipt || undefined}
      />

      {ownerActionDialog && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog modal-dialog-centered' role='document'>
            <div className='modal-content bg-white'>
              <div className='modal-header'>
                <h5 className='modal-title'>
                  {ownerActionDialog.action === 'APPROVE'
                    ? 'Owner Approve PO'
                    : ownerActionDialog.action === 'REWORK'
                      ? 'Owner Rework PO'
                      : 'Owner Reject PO'}
                </h5>
                <button type='button' className='btn-close' onClick={closeOwnerActionDialog}></button>
              </div>
              <div className='modal-body'>
                <div className='mb-3 text-dark'>
                  <div><strong>PO:</strong> {ownerActionDialog.po.poNumber}</div>
                  <div><strong>Vendor:</strong> {ownerActionDialog.po.vendor?.name || '-'}</div>
                </div>
                <label className='form-label fw-semibold'>
                  Remark {ownerActionDialog.action === 'APPROVE' ? '(optional)' : '*'}
                </label>
                <textarea
                  className='form-control'
                  rows={3}
                  value={ownerActionRemark}
                  onChange={(e) => setOwnerActionRemark(e.target.value)}
                  placeholder={
                    ownerActionDialog.action === 'APPROVE'
                      ? 'Optional approval remark'
                      : ownerActionDialog.action === 'REWORK'
                        ? 'Enter rework reason'
                        : 'Enter rejection reason'
                  }
                />
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={closeOwnerActionDialog} disabled={ownerActionSubmitting}>
                  Cancel
                </button>
                <button type='button' className='btn btn-primary' onClick={submitOwnerAction} disabled={ownerActionSubmitting}>
                  {ownerActionSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isBulkModalOpen && (
        <div className='modal fade show d-block' tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className='modal-dialog modal-md modal-dialog-centered'>
            <div className='modal-content text-dark' style={{ color: '#181C32' }}>
              <div className='modal-header'>
                <h5 className='modal-title fw-bold text-dark'>Bulk Download</h5>
                <button type='button' className='btn-close' onClick={() => setIsBulkModalOpen(false)} />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label fw-semibold text-dark'>Document Type</label>
                  <SearchableSelect
                    className='form-select'
                    value={bulkType}
                    onChange={(e) => setBulkType(e.target.value as any)}
                    disabled={isBulkDownloading}
                  >
                    {availableBulkTypes.map((type) => (
                      <option key={type} value={type}>
                        {type === 'PO'
                          ? 'Purchase Order'
                          : type === 'RFQ'
                            ? 'RFQ'
                            : type === 'REQUISITION'
                              ? 'Requisition'
                              : type === 'GR'
                                ? 'Goods Receipt'
                                : 'Invoice'}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
                <div className='row'>
                  <div className='col-md-6 mb-3'>
                    <label className='form-label fw-semibold text-dark'>From Date</label>
                    <input
                      type='date'
                      className='form-control'
                      value={bulkFrom}
                      onChange={(e) => setBulkFrom(e.target.value)}
                      disabled={isBulkDownloading}
                    />
                  </div>
                  <div className='col-md-6 mb-3'>
                    <label className='form-label fw-semibold text-dark'>To Date</label>
                    <input
                      type='date'
                      className='form-control'
                      value={bulkTo}
                      onChange={(e) => setBulkTo(e.target.value)}
                      disabled={isBulkDownloading}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-md-6 mb-3'>
                    <label className='form-label fw-semibold text-dark'>Company</label>
                    <input
                      type='text'
                      className='form-control'
                      value={bulkCompany}
                      onChange={(e) => setBulkCompany(e.target.value)}
                      disabled={isBulkDownloading}
                      placeholder='Optional company name'
                    />
                  </div>
                  <div className='col-md-6 mb-3'>
                    <label className='form-label fw-semibold text-dark'>Vessel</label>
                    <input
                      type='text'
                      className='form-control'
                      value={bulkVessel}
                      onChange={(e) => setBulkVessel(e.target.value)}
                      disabled={isBulkDownloading}
                      placeholder='Optional vessel name'
                    />
                  </div>
                  <div className='col-md-6 mb-3'>
                    <label className='form-label fw-semibold text-dark'>Vendor</label>
                    <input
                      type='text'
                      className='form-control'
                      value={bulkVendor}
                      onChange={(e) => setBulkVendor(e.target.value)}
                      disabled={isBulkDownloading}
                      placeholder='Optional vendor name'
                    />
                  </div>
                  <div className='col-md-6 mb-3'>
                    <label className='form-label fw-semibold text-dark'>Status</label>
                    <input
                      type='text'
                      className='form-control'
                      value={bulkStatus}
                      onChange={(e) => setBulkStatus(e.target.value)}
                      disabled={isBulkDownloading}
                      placeholder='Optional status'
                    />
                  </div>
                </div>
                <div className='alert alert-light border mb-0'>
                  Export applies filters, then downloads branded procurement PDFs for each matched record.
                </div>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-light' disabled={isBulkDownloading} onClick={() => setIsBulkModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className='btn btn-primary'
                  disabled={isBulkDownloading || !canDownloadType(bulkType)}
                  onClick={bulkDownload}
                >
                  {isBulkDownloading ? 'Preparing...' : 'Download'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {poDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='po-download-host'>
            <PurchaseOrderPARUDocument purchaseOrder={poDownloadDoc} />
          </div>
        </div>
      )}

      {rfqDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='po-rfq-download-host'>
            <QuotationDPCDocument rfq={rfqDownloadDoc} />
          </div>
        </div>
      )}

      {requisitionDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='po-requisition-download-host'>
            <RequisitionLECRDocument
              requisition={requisitionDownloadDoc.requisition}
              viewLines={requisitionDownloadDoc.lines}
              attachments={requisitionDownloadDoc.attachments}
            />
          </div>
        </div>
      )}

      {goodsReceiptDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='po-gr-download-host'>
            <GoodsReceiptGRNDocument receipt={goodsReceiptDownloadDoc.receipt} />
          </div>
        </div>
      )}

      {invoiceDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='po-invoice-download-host'>
            <InvoiceProcurementDocument
              invoice={invoiceDownloadDoc.invoice}
              purchaseOrder={invoiceDownloadDoc.purchaseOrder}
              goodsReceipt={invoiceDownloadDoc.goodsReceipt}
              requisition={invoiceDownloadDoc.requisition}
            />
          </div>
        </div>
      )}

      {/* Toast Container for notifications */}
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />
    </div>
  )
}

export { PurchaseOrdersList }
